/**
 * useNoteAutosave - HIPAA-Compliant Autosave Hook with Draft Recovery
 * 
 * Features:
 * 1. Silent autosave every 45 seconds + on field blur
 * 2. Encrypted sessionStorage cache for offline recovery
 * 3. No UI jumping or cursor resets
 * 4. Subtle "Saved" indicator
 * 5. Draft recovery prompt on page load
 * 6. Captures FULL form state: content, consent, treatment plan goals
 * 
 * @example
 * const { 
 *   onFieldBlur, 
 *   saveStatus, 
 *   SaveIndicator,
 *   DraftRecoveryModal 
 * } = useNoteAutosave({
 *   noteId: 'abc123',
 *   formData,              // Main form content
 *   consentData,           // Telehealth consent selections
 *   linkedGoals,           // Treatment plan goals
 *   enabled: true,
 *   onFormDataRestore: (restoredData) => {
 *     setFormData(restoredData.formData);
 *     setConsentData(restoredData.consentData);
 *     setLinkedGoals(restoredData.linkedGoals);
 *   }
 * });
 */

import { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import CryptoJS from 'crypto-js';
import { toast } from 'sonner';
import {
  deriveEncryptionKey as sharedDeriveEncryptionKey,
  encryptData as sharedEncryptData,
  decryptData as sharedDecryptData,
} from '../utils/storageCrypto';
import { scheduleIdle } from '../utils/scheduleIdle';

// Constants
//
// Jun 2026 — field types whose values are auto-populated by the editor or
// prefilled from the chart (mirrors backend AUTO_POPULATED_FIELD_TYPES).
// Used by the empty-note auto-restore check: a note whose only values live
// in these fields is still "empty" from the user's point of view.
const AUTO_POPULATED_FIELD_TYPES = new Set([
  'client_name', 'client_dob', 'client_age', 'client_email', 'client_phone',
  'client_address', 'client_allergies', 'client_diagnoses', 'client_medications',
  'create_task', 'provider_name', 'service_date', 'mrn',
]);

// Count fields carrying real USER-entered content.
const countRealUserFields = (formData) => {
  if (!formData || typeof formData !== 'object') return 0;
  const fields = Array.isArray(formData.fields)
    ? formData.fields
    : (formData.sections || []).flatMap((s) => s?.fields || []);
  let count = 0;
  for (const f of fields) {
    if (!f || typeof f !== 'object') continue;
    if (AUTO_POPULATED_FIELD_TYPES.has(f.type || '')) continue;
    const v = f.value;
    if (v === null || v === undefined) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) continue;
    if (v === (f.default_value ?? f.placeholder ?? undefined)) continue;
    count++;
  }
  return count;
};

// Feb 2026 perf tuning — increased server-autosave cadence from 5s
// to 30s.  Atlas profiler showed note_drafts at 663ms avg read /
// 452ms avg write across hundreds of ops; the 5s loop was the main
// driver.  Local sessionStorage cache (debounced 750ms after typing
// pause via cacheWriteTimeoutRef) still protects keystrokes for
// crash recovery.  Server save still also fires on field blur
// (1.5s debounce) and on window unload via sendBeacon, so data
// safety is unchanged — only latency to server is relaxed.
const AUTOSAVE_INTERVAL_MS = 30000; // 30 seconds — server cadence (local cache still typing-pause debounced)
const FIELD_BLUR_DEBOUNCE_MS = 1500; // 1.5 second debounce for field blur (faster response)
const MIN_SAVE_INTERVAL_MS = 5000; // Minimum 5 seconds between saves (matches new cadence floor)
const STORAGE_PREFIX = 'emr_draft_';

/**
 * Derive encryption key from JWT token
 * Uses a portion of the JWT as the encryption key
 */
const deriveEncryptionKey = sharedDeriveEncryptionKey;

/**
 * Encrypt data for sessionStorage (now shared via storageCrypto util).
 */
const encryptData = sharedEncryptData;

/**
 * Decrypt data from sessionStorage (now shared via storageCrypto util).
 */
const decryptData = sharedDecryptData;

/**
 * Get storage key for a note
 */
const getStorageKey = (noteId) => `${STORAGE_PREFIX}${noteId}`;

/**
 * Safely persist a draft to localStorage with LRU eviction.
 *
 * Feb 2026 — production providers hit `QuotaExceededError` because
 * drafts accumulated forever under the `emr_draft_` prefix (one
 * blob per note, encrypted ≈ 20-200 KB each).  When the browser's
 * 5-10 MB localStorage cap was reached, autosave silently lost
 * the draft on disk — directly causing data-loss bug reports.
 *
 * Strategy on quota failure:
 *   1. Walk the `emr_draft_*` keyspace, parse each value's
 *      `timestamp` (we wrote it inside the encrypted payload but
 *      also stamp a tiny metadata sidecar key for fast triage).
 *   2. Evict the oldest 25% of drafts.
 *   3. Retry the setItem.
 *   4. If still failing, evict aggressively (everything except the
 *      key we're writing) and retry once more.
 *   5. Last-resort: log and give up (server save will still run).
 *
 * Bonus: we also stamp a per-key `emr_draft_meta_<noteId>` JSON
 * sidecar holding `{ts}` so eviction is O(N keys) without paying
 * the decryption cost.  Sidecar is tiny (≈40 bytes); negligible
 * overhead vs. the encrypted payload.
 */
const _draftMetaKey = (noteId) => `emr_draft_meta_${noteId}`;

// Jun 2026 — BEACON FLUSH STAMP: sendBeacon gets no response, so we can't
// clear the draft on a successful tab-close flush.  Instead we stamp WHICH
// server state we flushed from; on next open, if the server's updated_at has
// moved past the stamped value, the flush (or a later save) landed and the
// surviving draft is stale.  Skew-free: compares server timestamps only.
const _flushStampKey = (noteId) => `emr_draft_flush_${noteId}`;

const stampBeaconFlush = (noteId, serverUpdatedAt) => {
  if (!noteId) return;
  try {
    localStorage.setItem(_flushStampKey(noteId), JSON.stringify({
      serverUpdatedAt: serverUpdatedAt || null,
      ts: Date.now(),
    }));
  } catch { /* best-effort */ }
};

const readBeaconFlushStamp = (noteId) => {
  try {
    const raw = localStorage.getItem(_flushStampKey(noteId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const _listDraftKeys = () => {
  const keys = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX) && !k.startsWith('emr_draft_meta_')) {
        keys.push(k);
      }
    }
  } catch { /* localStorage may be blocked */ }
  return keys;
};

const _draftTimestamp = (storageKey) => {
  try {
    const noteId = storageKey.slice(STORAGE_PREFIX.length);
    const meta = localStorage.getItem(_draftMetaKey(noteId));
    if (meta) {
      const parsed = JSON.parse(meta);
      if (parsed?.ts) return parsed.ts;
    }
  } catch { /* meta unreadable — fall through */ }
  // Fall back to "very old" so unmetered entries get evicted first.
  return 0;
};

const _evictOldestDrafts = (fraction = 0.25, keepKey = null) => {
  const keys = _listDraftKeys().filter(k => k !== keepKey);
  if (keys.length === 0) return 0;
  keys.sort((a, b) => _draftTimestamp(a) - _draftTimestamp(b));
  const cutoff = Math.max(1, Math.ceil(keys.length * fraction));
  let evicted = 0;
  for (let i = 0; i < cutoff; i++) {
    try {
      const k = keys[i];
      localStorage.removeItem(k);
      localStorage.removeItem(_draftMetaKey(k.slice(STORAGE_PREFIX.length)));
      evicted++;
    } catch { /* ignore single-key failures */ }
  }
  return evicted;
};

const safeSetDraft = (noteId, encryptedValue) => {
  const key = getStorageKey(noteId);
  const meta = JSON.stringify({ ts: Date.now() });
  const attempt = () => {
    localStorage.setItem(key, encryptedValue);
    try { localStorage.setItem(_draftMetaKey(noteId), meta); } catch { /* meta is best-effort */ }
  };
  try {
    attempt();
    return true;
  } catch (e) {
    // QuotaExceededError — start evicting.
    const evicted1 = _evictOldestDrafts(0.25, key);
    console.warn(`[Autosave] localStorage full, evicted ${evicted1} old drafts, retrying`);
    try {
      attempt();
      return true;
    } catch {
      // Second pass — aggressive: drop everything except current.
      const evicted2 = _evictOldestDrafts(1.0, key);
      console.warn(`[Autosave] still full, aggressively evicted ${evicted2} drafts, final retry`);
      try {
        attempt();
        return true;
      } catch (final) {
        console.error('[Autosave] localStorage cache permanently failed:', final?.message);
        return false;
      }
    }
  }
};

/**
 * Compute content hash for dirty checking
 * Normalizes values before hashing to prevent false positives from null vs "" differences
 */
const computeHash = (data) => {
  try {
    // Deep normalize the data before hashing
    const normalizeForHash = (obj) => {
      if (obj === null || obj === undefined) return '';
      if (typeof obj === 'string') return obj.trim();
      if (Array.isArray(obj)) {
        if (obj.length === 0) return '';
        return obj.map(item => normalizeForHash(item));
      }
      if (typeof obj === 'object') {
        const normalized = {};
        for (const key of Object.keys(obj).sort()) {
          normalized[key] = normalizeForHash(obj[key]);
        }
        return normalized;
      }
      return obj;
    };
    
    const normalized = normalizeForHash(data);
    const jsonString = JSON.stringify(normalized);
    return CryptoJS.MD5(jsonString).toString();
  } catch (e) {
    return Math.random().toString();
  }
};

/**
 * Save Status Types
 */
export const SaveStatus = {
  IDLE: 'idle',
  SAVING: 'saving',
  SAVED: 'saved',
  ERROR: 'error',
  OFFLINE_CACHED: 'offline_cached',
  SESSION_EXPIRED: 'session_expired',
  CONFLICT: 'conflict',  // Version conflict - another save happened while editing
};

/**
 * PURE FUNCTION: Check if two form data objects have meaningful differences
 * This is extracted as a pure function so it can be used before React hooks are initialized
 * Used by note switch handler to decide if caching is needed
 */
const checkMeaningfulDifference = (serverFormData, localFormData) => {
  // Normalize helper - treats null, undefined, "", whitespace as equivalent "empty"
  const normalizeForComparison = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.trim();
    if (Array.isArray(value)) {
      if (value.length === 0) return '';
      // For arrays, normalize each element
      return JSON.stringify(value.map(v => normalizeForComparison(v)));
    }
    if (typeof value === 'object') {
      if (Object.keys(value).length === 0) return '';
      // Sort keys for consistent comparison
      const sorted = {};
      for (const key of Object.keys(value).sort()) {
        sorted[key] = normalizeForComparison(value[key]);
      }
      return JSON.stringify(sorted);
    }
    return String(value);
  };
  
  // If either is missing, consider them different only if one has actual content
  if (!serverFormData && !localFormData) return false;
  if (!serverFormData) {
    // Check if local has any meaningful content
    const localFields = localFormData?.fields || [];
    return localFields.some(f => normalizeForComparison(f?.value) !== '');
  }
  if (!localFormData) {
    // Check if server has any meaningful content
    const serverFields = serverFormData?.fields || [];
    return serverFields.some(f => normalizeForComparison(f?.value) !== '');
  }
  
  // Compare fields
  const serverFields = serverFormData.fields || [];
  const localFields = localFormData.fields || [];
  
  // Check each field
  const maxLength = Math.max(serverFields.length, localFields.length);
  for (let i = 0; i < maxLength; i++) {
    const serverField = serverFields[i];
    const localField = localFields[i];
    
    const serverValue = normalizeForComparison(serverField?.value);
    const localValue = normalizeForComparison(localField?.value);
    
    if (serverValue !== localValue) {
      return true; // Found a meaningful difference
    }
  }
  
  // No meaningful differences found
  return false;
};

// Jun 2026 — CONFLICT AUTO-RETRY support: collect {fieldId: value} for
// non-empty USER-content fields (auto-populated header fields excluded).
const collectUserFieldValues = (fd) => {
  const out = {};
  if (!fd || typeof fd !== 'object') return out;
  const fields = Array.isArray(fd.fields) ? [...fd.fields] : [];
  for (const s of (fd.sections || [])) {
    if (s?.fields) fields.push(...s.fields);
  }
  for (const f of fields) {
    if (!f || !f.id) continue;
    if (AUTO_POPULATED_FIELD_TYPES.has(f.type || '')) continue;
    const v = f.value;
    if (v === null || v === undefined) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[f.id] = JSON.stringify(v);
  }
  return out;
};

// True when the local form preserves every non-empty user-content value
// present on the server — a version resync + retry then cannot clobber
// anyone else's work.
const localPreservesServerContent = (serverFd, localFd) => {
  const sv = collectUserFieldValues(serverFd);
  const lv = collectUserFieldValues(localFd);
  return Object.entries(sv).every(([id, val]) => lv[id] === val);
};

// Jun 2026 — DRAFT LOSS CHECK for silent auto-restore: counts server fields
// with real content that the draft would EMPTY.  If the draft blanks a
// meaningful share of saved content, silent restore is too risky — prompt.
const computeDraftLoss = (serverFormData, draftFormData) => {
  const collect = (fd) => {
    if (!fd) return [];
    return Array.isArray(fd.fields) ? fd.fields : (fd.sections || []).flatMap((s) => s?.fields || []);
  };
  const isEmptyVal = (v) => v === null || v === undefined ||
    (typeof v === 'string' && v.trim() === '') ||
    (Array.isArray(v) && v.length === 0);
  const draftById = {};
  for (const f of collect(draftFormData)) {
    if (f && f.id) draftById[f.id] = f.value;
  }
  let serverFilled = 0;
  let lost = 0;
  for (const f of collect(serverFormData)) {
    if (!f || !f.id || AUTO_POPULATED_FIELD_TYPES.has(f.type || '')) continue;
    if (isEmptyVal(f.value)) continue;
    serverFilled++;
    if (isEmptyVal(draftById[f.id])) lost++;
  }
  const isRisky = serverFilled > 0 && lost >= 2 && (lost / serverFilled) > 0.3;
  return { serverFilled, lost, isRisky };
};

/**
 * Main Autosave Hook
 * 
 * Now captures the FULL form state:
 * - formData: Main note content (fields, sections)
 * - consentData: Telehealth consent (provider_location, pos_code, modality)
 * - linkedGoals: Treatment plan goals array
 */
export function useNoteAutosave({
  noteId,
  setNoteId,                // NEW: Callback to update noteId after lazy creation
  formData,
  consentData = null,      // Telehealth consent selections
  linkedGoals = [],        // Treatment plan goals
  metadata = null,         // NEW: Note metadata (date_of_service, start_time, end_time, units, cpt_code)
  enabled = true,
  onFormDataRestore,
  getAuthToken, // Function to get current JWT token
  initialVersion = null,   // Initial version from server when note was loaded
  serverUpdatedAt = null,  // Server's updated_at timestamp for 60-second rule
  onVersionConflict,       // Callback when optimistic lock fails (409 conflict)
  // NEW: Data needed for lazy auto-creation
  createNoteData = null,   // { client_id, client_ids, provider_id, template_id, is_group_note, template_name }
  onNoteCreated = null,    // Callback after successful lazy creation
  // NEW: Loud failure handlers from NotesContext
  onSaveError = null,      // Callback to set autosaveBlockedReason in parent
  onSaveCriticalError = null, // Callback to set noteCreationError in parent (shows banner)
  // NEW: Client/Provider info for recovery banner display
  clientName = null,       // Client full name for "Found changes for [Client Name]" message
  providerName = null,     // Provider name for additional context
  // NEW: Client ID for cross-contamination prevention
  clientId = null,         // Client ID to verify against note's client_id on every save
  // Jun 2026: True when the server note was EXPLICITLY saved empty by the
  // user (manual save).  Controls empty-note auto-restore behavior.
  noteEmptyManuallySaved = false,
}) {
  // Refs to avoid re-renders
  const lastSavedHashRef = useRef(null);
  const lastSaveTimeRef = useRef(0);
  const sessionIdRef = useRef(crypto.randomUUID());
  // Jun 2026 — CONFLICT AUTO-RETRY: self-inflicted version conflicts from
  // slow overlapping saves resync + retry silently (max 2 per save cycle).
  const conflictRetryCountRef = useRef(0);
  const conflictRetryBypassRef = useRef(false); // skips MIN_SAVE_INTERVAL once
  const isSavingRef = useRef(false);
  const noteLoadedAtRef = useRef(null); // Track when note was loaded to prevent immediate saves
  const INITIAL_LOAD_DELAY_MS = 5000; // Wait 5 seconds after load before allowing autosave
  const isCreatingRef = useRef(false);  // NEW: Guard for lazy creation to prevent duplicates
  const pendingSaveRef = useRef(null); // Queue for saves that arrive while another is in progress
  const isInitializedRef = useRef(false); // CRITICAL: Prevent saves until note is fully initialized
  const fieldBlurTimeoutRef = useRef(null);
  const formDataRef = useRef(formData);
  // Workstream C3 hardening (Feb 2026) — local cache write is now
  // debounced behind these two refs to eliminate the per-keystroke
  // AES-256 encrypt + localStorage I/O that was causing input
  // stutter on long clinical notes.  Pattern: a silent isDirty
  // flag flipped on every formData change + a 750ms typing-pause
  // debounce that does the actual heavy work.
  const cacheWriteTimeoutRef = useRef(null);
  const isDirtyForCacheRef = useRef(false);
  const consentDataRef = useRef(consentData);
  const linkedGoalsRef = useRef(linkedGoals);
  const metadataRef = useRef(metadata);  // NEW: Track metadata for autosave
  const noteIdRef = useRef(noteId);     // NEW: Track noteId in ref for async operations
  const noteEmptyManuallySavedRef = useRef(noteEmptyManuallySaved);
  noteEmptyManuallySavedRef.current = noteEmptyManuallySaved;
  // Jun 2026 — draft RECOVERY runs regardless of edit mode (read-only opens,
  // e.g. billing's ?mode=view, must still surface drafts).  Save paths stay
  // gated on `enabled`; this ref lets recovery helpers branch on mode.
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  // Stable ref so the draft-check effect doesn't churn when callers pass
  // inline onFormDataRestore functions (new identity every render).
  const onFormDataRestoreRef = useRef(onFormDataRestore);
  onFormDataRestoreRef.current = onFormDataRestore;
  const clientIdRef = useRef(clientId);  // NEW: Track clientId for cross-contamination prevention
  const silentSaveRef = useRef(null);   // NEW: Track silentSave function to avoid dependency issues
  const serverUpdatedAtRef = useRef(serverUpdatedAt); // Track server's updated_at for 60-second rule
  const initialServerDataRef = useRef(null); // BASELINE: Capture initial server data for exit guard comparison
  // Feb 2026 perf — memoize the baseline hash so the 750ms cache
  // write debounce doesn't recompute a full deep-normalize +
  // JSON.stringify of the (stable) baseline on every typing pause.
  // For a 50KB form_data this saves ~10-20ms of main-thread work
  // per pause, which is the exact moment the user perceives as
  // keystroke lag.  Invalidated whenever initialServerDataRef is
  // reassigned (in 3 places below).
  const initialServerHashRef = useRef(null);
  // Feb 2026 perf — Option 2 (content guard).  Tracks the hash of
  // the form data we last successfully persisted to localStorage so
  // we can short-circuit redundant encrypt+write passes when the
  // form hasn't changed since the prior cache write.  Different
  // from ``lastSavedHashRef`` which tracks SERVER saves.
  const lastCachedHashRef = useRef(null);
  // Feb 2026 perf — Option 3 (idle deferral).  Holds the active
  // requestIdleCallback handle so we can cancel it when:
  //   • a newer typing-pause re-schedules
  //   • the note is closed mid-flight (component unmount)
  //   • a forced flush (blur/unload/save) wants the cache write
  //     to land synchronously instead of waiting for browser idle.
  const idleCallbackRef = useRef(null);
  // Tracks the most recent pending payload for the idle callback —
  // lets a forced-flush helper read whatever the latest snapshot
  // is even when the timeout itself hasn't fired yet.
  const pendingCacheWriteRef = useRef(null);
  
  // Keep serverUpdatedAt ref in sync
  useEffect(() => {
    serverUpdatedAtRef.current = serverUpdatedAt;
  }, [serverUpdatedAt]);

  // Keep clientId ref in sync
  useEffect(() => {
    clientIdRef.current = clientId;
  }, [clientId]);
  
  // NEW: Track changed fields for delta updates
  const lastSavedFormDataRef = useRef(null); // Snapshot of last saved form data
  const changedFieldsRef = useRef(new Set()); // Track which fields changed since last save
  
  // SAVE LOOP PREVENTION: Track when we just saved to block re-detection
  const justSavedRef = useRef(false);
  
  // Track if we're in the middle of a note switch (to prevent false change detection)
  const noteSwitchInProgressRef = useRef(false);
  
  // OPTIMISTIC LOCKING: Track the version we're working with
  const currentVersionRef = useRef(initialVersion);
  
  // IMMEDIATE SYNC: Update version ref synchronously if it doesn't match
  // This ensures we have the correct version BEFORE any useEffect runs.
  // Jun 2026 — UPGRADE-ONLY for the same note: a stale initialVersion prop
  // (host hasn't refetched the note yet) must not DOWNGRADE the ref after a
  // successful save or conflict auto-resync bumped it — that downgrade was
  // the root cause of self-inflicted version conflicts on rapid saves.
  if (noteId && noteId === noteIdRef.current && initialVersion !== currentVersionRef.current) {
    const _cur = currentVersionRef.current;
    if (_cur === null || _cur === undefined ||
        (typeof initialVersion === 'number' && initialVersion > _cur)) {
      void 0 && console.log('[Autosave] Immediate version sync:', {
        from: _cur,
        to: initialVersion
      });
      currentVersionRef.current = initialVersion;
    }
  }
  
  // Detect note switch and mark it
  if (noteId !== noteIdRef.current) {
    noteSwitchInProgressRef.current = true;
    // CRITICAL: Immediately block saves when note is switching
    isInitializedRef.current = false;
  }
  
  // COMPREHENSIVE NOTE SWITCH HANDLER
  // Resets ALL tracking state when switching between notes to prevent:
  // 1. Cross-note session contamination
  // 2. False "unsaved changes" detection
  // 3. Version mismatch errors (v9 vs v2)
  useEffect(() => {
    // Log the current state for debugging version conflicts
    void 0 && console.log('[Autosave] Note switch check:', {
      noteId: noteId?.slice?.(0, 8) || noteId,
      noteIdRef: noteIdRef.current?.slice?.(0, 8) || noteIdRef.current,
      initialVersion,
      currentVersionRef: currentVersionRef.current
    });
    
    // Helper function to save pending changes before note switch
    const savePendingChangesBeforeSwitch = (previousNoteId, previousFormData, previousConsent, previousGoals) => {
      if (!previousNoteId || !previousFormData) return;
      
      // Clear any pending debounce timer
      if (fieldBlurTimeoutRef.current) {
        clearTimeout(fieldBlurTimeoutRef.current);
        fieldBlurTimeoutRef.current = null;
      }
      
      // Check if there are real changes worth saving
      const hasRealChanges = previousFormData && 
                             initialServerDataRef.current && 
                             checkMeaningfulDifference(initialServerDataRef.current, previousFormData);
      
      if (hasRealChanges) {
        void 0 && console.log('[Autosave] 💾 Meaningful changes detected, saving before switch...');
        
        // 1. Cache locally first (always works)
        try {
          const token = getAuthToken?.() || localStorage.getItem('token');
          const key = deriveEncryptionKey(token);
          const draftData = {
            noteId: previousNoteId,
            formData: previousFormData,
            consentData: previousConsent || null,
            linkedGoals: previousGoals || [],
            timestamp: new Date().toISOString(),
            sessionId: sessionIdRef.current,
          };
          const encrypted = encryptData(draftData, key);
          if (encrypted) {
            safeSetDraft(previousNoteId, encrypted);
            void 0 && console.log('[Autosave] ✅ Previous note cached locally');
          }
        } catch (e) {
          console.warn('[Autosave] Failed to cache previous note:', e);
        }
        
        // 2. Also trigger server save via sendBeacon (fire and forget)
        try {
          const token = getAuthToken?.() || localStorage.getItem('token');
          if (token) {
            const beaconPayload = JSON.stringify({
              form_data: previousFormData,
              consent_data: previousConsent,
              linked_goals: previousGoals,
              consent_touched: changedFieldsRef.current.has('__consent_data__'),
              save_trigger: 'note_switch',
              session_id: sessionIdRef.current,
              is_delta: false,
              expected_version: currentVersionRef.current,
              auth_token: token,
            });
            const blob = new Blob([beaconPayload], { type: 'application/json' });
            const queued = navigator.sendBeacon(
              `${process.env.REACT_APP_BACKEND_URL}/api/notes/${previousNoteId}/beacon-save`,
              blob
            );
            if (queued) {
              stampBeaconFlush(previousNoteId, serverUpdatedAtRef.current);
              void 0 && console.log('[Autosave] 🚀 Beacon save queued for previous note');
            }
          }
        } catch (e) {
          console.warn('[Autosave] Beacon save failed:', e);
        }
      } else {
        void 0 && console.log('[Autosave] ⏭️ No meaningful changes - skipping save on note switch');
      }
    };
    
    // CASE 1: Switching FROM one existing note TO another existing note
    if (noteId && noteIdRef.current && noteIdRef.current !== noteId) {
      void 0 && console.log('[Autosave] 🔄 Switching Note Context (existing → existing):', { old: noteIdRef.current, new: noteId });
      
      // Save pending changes from previous note before switching
      savePendingChangesBeforeSwitch(
        noteIdRef.current,
        formDataRef.current,
        consentDataRef.current,
        linkedGoalsRef.current
      );
      
      // Full reset for new note context
      sessionIdRef.current = crypto.randomUUID();
      noteIdRef.current = noteId;
      noteLoadedAtRef.current = Date.now(); // Mark when this note was loaded
      lastSavedHashRef.current = null;
      lastSavedFormDataRef.current = null;
      lastSaveTimeRef.current = 0;
      conflictRetryCountRef.current = 0;
      changedFieldsRef.current.clear();
      formDataRef.current = null; // CRITICAL: Clear form data to prevent bleed
      initialServerDataRef.current = null; // BASELINE: Will be set when form data first arrives
      initialServerHashRef.current = null;  // perf — invalidated when baseline reassigned
      hasUnsavedChangesRef.current = false;
      setVersionConflict(null);
      setSaveStatus(SaveStatus.IDLE);
      currentVersionRef.current = initialVersion ?? null;
      // CRITICAL: Reset draft recovery state to prevent stale banner from previous note
      setShowDraftRecovery(false);
      setRecoveredDraft(null);
      
      void 0 && console.log('[Autosave] ✅ Note context reset complete. Version set to:', currentVersionRef.current);
      
      // Clear the note switch flag AFTER all refs are updated
      noteSwitchInProgressRef.current = false;
      
      // FIX A: Content-based initialization instead of arbitrary timeout
      // isInitializedRef will be set to true when form data actually arrives (see formData effect below)
      // This is more reliable than a 100ms timeout which can be too fast for large forms
      void 0 && console.log('[Autosave] 🕐 Waiting for form data before allowing saves...');
    }
    // CASE 2: Switching FROM an existing note TO a NEW note (noteId becomes null)
    // This happens when user closes a note and opens a fresh "Create Note" dialog
    else if (!noteId && noteIdRef.current) {
      void 0 && console.log('[Autosave] 🔄 Switching to NEW note (clearing previous context):', { old: noteIdRef.current });
      
      // Save pending changes from previous note before switching
      savePendingChangesBeforeSwitch(
        noteIdRef.current,
        formDataRef.current,
        consentDataRef.current,
        linkedGoalsRef.current
      );
      
      // Full reset - CRITICAL to prevent data bleed to new note
      sessionIdRef.current = crypto.randomUUID();
      noteIdRef.current = null;
      noteLoadedAtRef.current = Date.now(); // Mark load time even for new notes
      lastSavedHashRef.current = null;
      lastSavedFormDataRef.current = null;
      lastSaveTimeRef.current = 0;
      conflictRetryCountRef.current = 0;
      changedFieldsRef.current.clear();
      formDataRef.current = null; // CRITICAL: Clear form data to prevent bleed
      setVersionConflict(null);
      setSaveStatus(SaveStatus.IDLE);
      currentVersionRef.current = null;
      // CRITICAL: Reset draft recovery state to prevent stale banner from previous note
      setShowDraftRecovery(false);
      setRecoveredDraft(null);
      
      void 0 && console.log('[Autosave] ✅ Context cleared for new note');
      
      // Clear the note switch flag
      noteSwitchInProgressRef.current = false;
      
      // CRITICAL: Mark as initialized after state is set
      setTimeout(() => {
        isInitializedRef.current = true;
        void 0 && console.log('[Autosave] ✅ New note context initialized, saves now allowed');
      }, 100);
    }
    // CASE 3: NEW note just got its ID assigned (lazy creation)
    // OR: Opening an existing note for the first time (no previous noteIdRef)
    // CRITICAL FIX: Generate a fresh session ID for the new note
    // Previously this case did NOT reset session, causing cross-note contamination
    else if (noteId && !noteIdRef.current) {
      void 0 && console.log('[Autosave] 📝 Note ID assigned (new or first open):', {
        noteId: noteId.slice(0, 8) + '...',
        initialVersion,
        currentVersionRef: currentVersionRef.current
      });
      
      // CRITICAL: Generate fresh session ID for this note
      // This prevents session reuse when rapidly creating/switching notes
      const oldSessionId = sessionIdRef.current;
      sessionIdRef.current = crypto.randomUUID();
      noteLoadedAtRef.current = Date.now(); // Mark when this note was loaded
      void 0 && console.log('[Autosave] 🔑 Generated fresh session ID:', {
        oldSession: oldSessionId?.slice(0, 8) + '...',
        newSession: sessionIdRef.current.slice(0, 8) + '...'
      });
      
      noteIdRef.current = noteId;
      
      // Reset tracking state for clean slate
      lastSavedHashRef.current = null;
      lastSavedFormDataRef.current = null;
      changedFieldsRef.current.clear();
      // CRITICAL: Reset draft recovery state to prevent stale banner from previous note
      setShowDraftRecovery(false);
      setRecoveredDraft(null);
      
      // CRITICAL: Set version to initialVersion (for existing notes) or null (for new notes)
      currentVersionRef.current = initialVersion ?? null;
      void 0 && console.log('[Autosave] ✅ Version set to:', currentVersionRef.current);
      
      // Clear the note switch flag
      noteSwitchInProgressRef.current = false;
      
      // CRITICAL: Mark as initialized after all refs are set
      setTimeout(() => {
        isInitializedRef.current = true;
        void 0 && console.log('[Autosave] ✅ Note ID assigned and initialized, saves now allowed');
      }, 100);
    }
  }, [noteId, initialVersion, getAuthToken]);
  
  // CRITICAL: Sync currentVersionRef when initialVersion prop changes
  // This allows conflict handlers to update the version by changing the prop.
  // Jun 2026 — UPGRADE-ONLY (see immediate sync above): a stale prop must
  // not downgrade the ref after a save/auto-resync advanced it.
  useEffect(() => {
    if (initialVersion !== null && initialVersion !== undefined && currentVersionRef.current !== initialVersion) {
      const cur = currentVersionRef.current;
      if (cur !== null && cur !== undefined &&
          typeof initialVersion === 'number' && initialVersion < cur) {
        void 0 && console.log('[Autosave] ⏭️ Ignoring stale version prop downgrade:', { cur, initialVersion });
        return;
      }
      void 0 && console.log('[Autosave] 🔄 Version sync from prop:', {
        oldVersion: cur,
        newVersion: initialVersion,
        noteId: noteIdRef.current?.slice?.(0, 8)
      });
      currentVersionRef.current = initialVersion;
    }
  }, [initialVersion]);
  
  // State for UI (minimal to prevent re-renders)
  const [saveStatus, setSaveStatus] = useState(SaveStatus.IDLE);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [showDraftRecovery, setShowDraftRecovery] = useState(false);
  // Jun 2026 — NO RESTORE DIALOG on note open: while a draft candidate is
  // being validated/restored, hosts keep the note in its loading state so
  // the user only ever sees the fully-restored content (no dialog, no
  // flash of pre-restore content).
  const [draftRestorePending, setDraftRestorePending] = useState(false);
  // Render-time seed (React "adjust state during render" pattern): when the
  // noteId changes, peek localStorage synchronously so the very first render
  // already holds the loading state — no flash of pre-restore content.
  const draftPeekNoteIdRef = useRef(undefined);
  if (draftPeekNoteIdRef.current !== noteId) {
    draftPeekNoteIdRef.current = noteId;
    let candidate = false;
    if (noteId) {
      try { candidate = !!window.localStorage.getItem(getStorageKey(noteId)); } catch { candidate = false; }
    }
    if (candidate !== draftRestorePending) setDraftRestorePending(candidate);
  }
  const [recoveredDraft, setRecoveredDraft] = useState(null);
  const [versionConflict, setVersionConflict] = useState(null); // { serverVersion, localVersion }
  
  // FIX C: hasUnsavedChanges is now a ref that gets computed from hasMeaningfulDifference
  // This ensures it's always in sync with actual content differences, not just "touched" state
  const hasUnsavedChangesRef = useRef(false);
  
  // DEEP EQUALITY HELPER: Normalize values for comparison
  // Treats null, undefined, "", and whitespace-only as equivalent "empty"
  const normalizeValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.trim();
    if (Array.isArray(value)) return value.length === 0 ? '' : JSON.stringify(value);
    if (typeof value === 'object') return Object.keys(value).length === 0 ? '' : JSON.stringify(value);
    return String(value);
  };
  
  const valuesEqual = (a, b) => normalizeValue(a) === normalizeValue(b);
  
  // Keep refs updated and track changes
  useEffect(() => {
    const prevData = formDataRef.current;
    
    // CRITICAL: Skip change detection if note switch is in progress
    // This prevents false positives when switching notes (formData arrives before refs update)
    if (noteSwitchInProgressRef.current) {
      void 0 && console.log('[Autosave] Skipping change detection - note switch in progress');
      return;
    }
    
    // CRITICAL: Skip change detection if noteId doesn't match noteIdRef
    // This is a backup check for race conditions
    if (noteId && noteIdRef.current && noteId !== noteIdRef.current) {
      void 0 && console.log('[Autosave] Skipping change detection - noteId mismatch', {
        noteId: noteId?.slice?.(0, 8),
        noteIdRef: noteIdRef.current?.slice?.(0, 8)
      });
      return;
    }
    
    // CRITICAL FIX: Don't update formDataRef to empty/null values
    // This prevents the race condition where Cancel button sets {} and then
    // the debounced save fires and saves the empty content
    const hasValidFormData = formData && 
                             typeof formData === 'object' && 
                             Object.keys(formData).length > 0 &&
                             (formData.fields?.length > 0 || formData.sections?.length > 0);
    
    if (hasValidFormData) {
      formDataRef.current = formData;
      
      // FIX A: Content-based initialization - mark as initialized when form data arrives
      // This is more reliable than a 100ms timeout which can be too fast for large forms
      if (!isInitializedRef.current) {
        isInitializedRef.current = true;
        void 0 && console.log('[Autosave] ✅ Note fully initialized (form data received), saves now allowed');
      }
    }
    
    // Initialize lastSavedFormData if not set (first time we have valid formData)
    if (hasValidFormData && !lastSavedFormDataRef.current) {
      lastSavedFormDataRef.current = JSON.parse(JSON.stringify(formData));
    }
    
    // BASELINE LOCKING: Only capture initialServerDataRef AFTER note is fully initialized
    // This prevents "partial baseline" false positives where we capture incomplete form data
    // that makes the note look dirty as soon as the rest of the fields load
    if (hasValidFormData && isInitializedRef.current && !initialServerDataRef.current) {
      initialServerDataRef.current = JSON.parse(JSON.stringify(formData));
      initialServerHashRef.current = null;  // perf — recompute lazily on first cache write
      void 0 && console.log('[Autosave] 📸 Captured initial server data baseline (note fully initialized)');
    }
    
    // DEBOUNCED LOCAL CACHE: Cache to localStorage AFTER a natural
    // typing pause (~750ms) instead of synchronously on every
    // keystroke.  The original implementation here ran a full
    // computeHash() × 2 + JSON.stringify() + AES-256 encrypt +
    // localStorage write on every formData mutation — for a large
    // clinical note that's enough sync work per keystroke to be felt
    // as input stutter.
    //
    // Pattern (per Feb 2026 user feedback):
    //   1. Flip a silent isDirtyForCacheRef = true (cheap, synchronous)
    //   2. Schedule the actual heavy work via setTimeout(..., 750)
    //   3. Cancel + reschedule on every subsequent keystroke
    //   4. The work only runs after the user pauses typing
    //
    // Data safety: the existing 5s interval autosave + onFieldBlur
    // debounce + window-unload beacon still all run independently
    // of this local cache.  This debounce is purely about latency,
    // not correctness — the local cache becomes eventually-consistent
    // within 750ms of the last keystroke instead of always-current.
    if (hasValidFormData && isInitializedRef.current && noteIdRef.current) {
      isDirtyForCacheRef.current = true;
      if (cacheWriteTimeoutRef.current) {
        clearTimeout(cacheWriteTimeoutRef.current);
      }
      cacheWriteTimeoutRef.current = setTimeout(() => {
        cacheWriteTimeoutRef.current = null;
        // Re-read the latest formData via ref (it may have changed
        // again since this timeout was scheduled) and bail if the
        // note has since been closed.
        const latestFormData = formDataRef.current;
        if (!latestFormData || !isInitializedRef.current || !noteIdRef.current) {
          return;
        }
        if (!isDirtyForCacheRef.current) {
          return; // a prior tick already flushed this round
        }
        // Now do the heavy work (hash + encrypt + I/O) — but only
        // if the data actually differs from the baseline.
        const baselineData = initialServerDataRef.current;
        if (!baselineData) {
          isDirtyForCacheRef.current = false;
          return;
        }
        const currentHash = computeHash(latestFormData);
        // ── Guard 1 (Feb 2026, Option 2 — content guard) ────────
        // Skip the entire encrypt+write pass when the form hasn't
        // changed since the LAST successful cache write.  Without
        // this, idle taps / focus changes that re-trigger the
        // debounced cache write would burn CPU re-encrypting the
        // same payload.  Different from the baseline check below
        // (which says "is this still original?") — this asks "did
        // we already cache this exact value?".
        if (lastCachedHashRef.current && currentHash === lastCachedHashRef.current) {
          isDirtyForCacheRef.current = false;
          return;
        }
        // Feb 2026 perf — memoized.  Recomputing the baseline's hash
        // on every typing pause was a hidden O(n) traversal +
        // JSON.stringify of a stable input.  Cache it the first time
        // we see this baseline; invalidated whenever the baseline
        // ref is reassigned (3 sites above + below).
        if (initialServerHashRef.current == null) {
          initialServerHashRef.current = computeHash(baselineData);
        }
        const baselineHash = initialServerHashRef.current;
        if (currentHash === baselineHash) {
          isDirtyForCacheRef.current = false;
          return;
        }
        // Capture a stable snapshot for the deferred work — the
        // refs may change again before requestIdleCallback fires
        // (and we want to write what the user paused on, not what
        // they typed next).
        const snapshot = {
          noteId: noteIdRef.current,
          formData: latestFormData,
          consentData: consentDataRef.current || null,
          linkedGoals: linkedGoalsRef.current || [],
          timestamp: new Date().toISOString(),
          sessionId: sessionIdRef.current,
          hash: currentHash,
        };
        pendingCacheWriteRef.current = snapshot;
        // Cancel any prior pending idle callback — only the latest
        // pause's snapshot should win.  This is critical: without
        // canceling, two rapid pauses would race and the older one
        // might land AFTER the newer one, clobbering fresh data.
        if (idleCallbackRef.current) {
          try { idleCallbackRef.current(); } catch {}
          idleCallbackRef.current = null;
        }
        // ── Guard 2 (Feb 2026, Option 3 — idle deferral) ────────
        // Push the expensive AES encrypt + synchronous setItem off
        // the active animation frame.  Native rIC on Chrome / FF,
        // setTimeout(1) fallback on Safari.  Timeout of 2s ensures
        // the work runs even on a perpetually-busy main thread —
        // so the cache cannot get permanently starved.
        idleCallbackRef.current = scheduleIdle(() => {
          idleCallbackRef.current = null;
          const work = pendingCacheWriteRef.current;
          if (!work || work.noteId !== noteIdRef.current) {
            // Note was closed or swapped while we waited — abort.
            pendingCacheWriteRef.current = null;
            return;
          }
          try {
            const token = getAuthToken?.() || localStorage.getItem('token');
            const key = deriveEncryptionKey(token);
            const encrypted = encryptData({
              noteId: work.noteId,
              formData: work.formData,
              consentData: work.consentData,
              linkedGoals: work.linkedGoals,
              timestamp: work.timestamp,
              sessionId: work.sessionId,
            }, key);
            if (encrypted) {
              safeSetDraft(work.noteId, encrypted);
              lastCachedHashRef.current = work.hash;
            }
          } catch (e) {
            // Silent fail — local cache is a safety net, not the
            // primary save path (server saves on blur/interval/
            // unload remain untouched).
            void 0 && console.error('[Autosave] idle cache write failed:', e);
          } finally {
            pendingCacheWriteRef.current = null;
            isDirtyForCacheRef.current = false;
          }
        }, { timeout: 2000 });
        return;  // ── early-return — the legacy synchronous encrypt
                 //    block below is skipped now that idle defers
                 //    the work.  Kept guards above intact for the
                 //    "skip entirely" cases (unchanged + baseline).
        try {
          const token = getAuthToken?.() || localStorage.getItem('token');
          const key = deriveEncryptionKey(token);
          const draftData = {
            noteId: noteIdRef.current,
            formData: latestFormData,
            consentData: consentDataRef.current || null,
            linkedGoals: linkedGoalsRef.current || [],
            timestamp: new Date().toISOString(),
            sessionId: sessionIdRef.current,
          };
          const encrypted = encryptData(draftData, key);
          if (encrypted) {
            safeSetDraft(noteIdRef.current, encrypted);
          }
        } catch (e) {
          // Silent fail - this is a safety net, not critical path
        }
        isDirtyForCacheRef.current = false;
      }, 750);
    }
    
    // REMOVED: All automatic hasUnsavedChanges computation from this effect
    // This was causing the exit guard to show even after save because:
    // 1. Save completes, sets hasUnsavedChangesRef = false
    // 2. This effect runs (formData reference changed)
    // 3. Effect computes diff, sets hasUnsavedChangesRef = true
    // 4. Exit guard sees true, shows dialog
    //
    // Instead, hasRealUnsavedChanges() does a FRESH comparison when the exit guard checks
    
  }, [formData]);
  
  useEffect(() => {
    const prevConsent = consentDataRef.current;
    consentDataRef.current = consentData;
    
    // Track consent changes
    if (prevConsent && consentData) {
      if (JSON.stringify(prevConsent) !== JSON.stringify(consentData)) {
        changedFieldsRef.current.add('__consent_data__');
      }
    }
  }, [consentData]);
  
  useEffect(() => {
    const prevGoals = linkedGoalsRef.current;
    linkedGoalsRef.current = linkedGoals;
    
    // Track goals changes
    if (prevGoals && linkedGoals) {
      if (JSON.stringify(prevGoals) !== JSON.stringify(linkedGoals)) {
        changedFieldsRef.current.add('__linked_goals__');
      }
    }
  }, [linkedGoals]);
  
  // Keep metadata ref in sync
  useEffect(() => {
    const prevMetadata = metadataRef.current;
    metadataRef.current = metadata;
    
    // Track metadata changes (date_of_service, times, etc.)
    if (prevMetadata && metadata) {
      if (JSON.stringify(prevMetadata) !== JSON.stringify(metadata)) {
        changedFieldsRef.current.add('__metadata__');
        // Jun 2026 — a metadata-only edit (DOS/times/units) must count as
        // dirty so blur/interval saves fire and persist it without needing
        // the Save Changes button.
        hasUnsavedChangesRef.current = true;
      }
    }
  }, [metadata]);
  
  // Get encryption key from token
  const encryptionKey = useMemo(() => {
    const token = getAuthToken?.() || localStorage.getItem('token');
    return deriveEncryptionKey(token);
  }, [getAuthToken]);
  
  /**
   * Cache draft to encrypted localStorage (persistent storage)
   * This is our "last resort" recovery if internet cuts out OR browser crashes
   * Uses localStorage instead of sessionStorage for persistence across browser restarts
   * Now includes FULL form state: content, consent, and goals
   * ALSO saves to server for cross-browser/cross-user recovery by admins
   * INCLUDES client/provider names for recovery banner display
   */
  const cacheDraftLocally = useCallback((data, consent, goals) => {
    const currentNoteId = noteIdRef.current;
    if (!currentNoteId || !data) return;
    
    const timestamp = new Date().toISOString();
    
    try {
      const draftData = {
        noteId: currentNoteId,
        formData: data,
        consentData: consent || null,
        linkedGoals: goals || [],
        timestamp: timestamp,
        sessionId: sessionIdRef.current,
        // Include client/provider names for recovery banner "Found changes for [Client Name]"
        clientName: clientName || null,
        providerName: providerName || null,
        // Include field counts for recovery UI
        fieldCounts: {
          formFields: Object.keys(data || {}).length,
          hasConsent: !!(consent && (consent.provider_location || consent.pos_code || consent.modality)),
          goalsCount: (goals || []).length,
        },
      };
      
      const encrypted = encryptData(draftData, encryptionKey);
      if (encrypted) {
        // Use localStorage for persistence across browser restarts/crashes
        localStorage.setItem(getStorageKey(currentNoteId), encrypted);
      }
    } catch (e) {
      console.warn('[Autosave] Failed to cache draft locally:', e);
    }
    
    // ALSO save to server for cross-browser/admin recovery (fire and forget)
    // This runs in background and doesn't block the main autosave flow
    try {
      const token = getAuthToken?.() || localStorage.getItem('token');
      if (token) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notes/${currentNoteId}/draft`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            form_data: data,
            consent_data: consent,
            linked_goals: goals,
            client_side_timestamp: timestamp,
          }),
        }).catch(err => {
          // Silent fail - server draft is a backup, not critical path
          console.debug('[Autosave] Server draft save failed (non-critical):', err.message);
        });
      }
    } catch (e) {
      // Ignore - server draft is best-effort
    }
  }, [noteId, encryptionKey, getAuthToken, clientName, providerName]);
  
  /**
   * Clear local draft cache (after successful server save or restore)
   * Also clears server-side draft
   */
  const clearLocalDraft = useCallback((localOnly = false) => {
    if (!noteId) return;
    
    // Clear localStorage (payload + LRU meta sidecar + beacon flush stamp)
    try {
      localStorage.removeItem(getStorageKey(noteId));
      localStorage.removeItem(_draftMetaKey(noteId));
      localStorage.removeItem(_flushStampKey(noteId));
    } catch (e) {
      // Ignore
    }
    
    // Clear server-side draft (fire and forget).
    // Jun 2026 — guarded: open-time validation branches pass localOnly=true
    // (they judge only the LOCAL copy; the server draft may still be valid
    // admin-recoverable work), and read-only opens never write back.
    if (localOnly || !enabledRef.current) return;
    try {
      const token = getAuthToken?.() || localStorage.getItem('token');
      if (token) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notes/${noteId}/draft`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }).catch(() => {
          // Silent fail
        });
      }
    } catch (e) {
      // Ignore
    }
  }, [noteId, getAuthToken]);
  
  /**
   * MEANINGFUL DIFFERENCE CHECK
   * Compares local backup vs server data to determine if banner should show.
   * Rules:
   * 1. Normalization: null, undefined, "", and whitespace-only are treated as identical
   * 2. Field Count: Both must have same fields, with same values after normalization
   * 3. Timestamp Ignorance: Content is the only truth, timestamps don't matter
   */
  const hasMeaningfulDifference = useCallback((serverFormData, localFormData) => {
    // Helper to deeply normalize a value for comparison
    const normalizeDeep = (value) => {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string') return value.trim();
      if (Array.isArray(value)) {
        if (value.length === 0) return '';
        // Recursively normalize array items
        return value.map(item => normalizeDeep(item));
      }
      if (typeof value === 'object') {
        const result = {};
        for (const key of Object.keys(value).sort()) {
          const normalized = normalizeDeep(value[key]);
          // Only include non-empty values
          if (normalized !== '' && normalized !== null && 
              !(Array.isArray(normalized) && normalized.length === 0)) {
            result[key] = normalized;
          }
        }
        return Object.keys(result).length === 0 ? '' : result;
      }
      return value;
    };
    
    // Extract just the field values from formData (ignore structure metadata)
    const extractFieldValues = (formData) => {
      if (!formData) return {};
      const values = {};
      
      // Handle flat fields array
      if (formData.fields && Array.isArray(formData.fields)) {
        formData.fields.forEach(field => {
          if (field.id) {
            values[field.id] = normalizeDeep(field.value);
          }
        });
      }
      
      // Handle sections-based structure
      if (formData.sections && Array.isArray(formData.sections)) {
        formData.sections.forEach(section => {
          if (section.fields && Array.isArray(section.fields)) {
            section.fields.forEach(field => {
              if (field.id) {
                values[field.id] = normalizeDeep(field.value);
              }
            });
          }
        });
      }
      
      return values;
    };
    
    const serverValues = extractFieldValues(serverFormData);
    const localValues = extractFieldValues(localFormData);
    
    // Get all unique field IDs from both
    const allFieldIds = new Set([...Object.keys(serverValues), ...Object.keys(localValues)]);
    
    // Check each field for meaningful differences
    for (const fieldId of allFieldIds) {
      const serverVal = serverValues[fieldId] ?? '';
      const localVal = localValues[fieldId] ?? '';
      
      // Compare normalized JSON strings
      const serverStr = JSON.stringify(serverVal);
      const localStr = JSON.stringify(localVal);
      
      if (serverStr !== localStr) {
        void 0 && console.log(`[Autosave] Meaningful difference found in field "${fieldId}":`, {
          server: serverVal,
          local: localVal
        });
        return true; // Found a meaningful difference
      }
    }
    
    void 0 && console.log('[Autosave] No meaningful differences found between server and local backup');
    return false; // No meaningful differences
  }, []);
  
  /**
   * Check for existing local draft on mount
   * REFACTORED P0 STABILITY UPDATE:
   * - REMOVED 60-Second Rule (was causing silent data loss)
   * - IGNORES SessionID (new login shouldn't prevent recovery)
   * - Uses noteId + content comparison only
   * 
   * ANTI-FLICKER LOGIC:
   * - 10-Second Buffer: Only show banner if draft is 10+ seconds newer (accounts for network latency)
   * - Hash Consistency: If content is identical, NEVER show banner regardless of timestamps
   * - This function is only called on Initial Mount (controlled by useEffect dependency)
   * 
   * Returns draft from localStorage if:
   * 1. Draft timestamp is at least 10 seconds newer than server (network latency buffer), AND
   * 2. Draft has MEANINGFUL differences from server data (hash check is AUTHORITATIVE)
   */
  const checkForLocalDraft = useCallback(() => {
    if (!noteId) return null;
    
    // ANTI-FLICKER: 10-second buffer accounts for network latency during saves
    // This prevents the banner from flickering when server is still processing the save
    const NETWORK_LATENCY_BUFFER_SECONDS = 10;
    
    try {
      void 0 && console.log(`[Autosave] checkForLocalDraft: Looking for key ${getStorageKey(noteId)}`);
      const encrypted = localStorage.getItem(getStorageKey(noteId));
      if (!encrypted) {
        void 0 && console.log('[Autosave] checkForLocalDraft: No encrypted data found');
        return null;
      }
      
      const draft = decryptData(encrypted, encryptionKey);
      if (!draft || !draft.formData) {
        // Decryption failed (likely token changed) — clear stale entry
        try { sessionStorage.removeItem(getStorageKey(noteId)); } catch {}
        return null;
      }
      
      // Verify it's for this note (noteId is the ONLY identity check - sessionId is IGNORED)
      if (draft.noteId !== noteId) {
        // MISMATCH DETECTED: This draft belongs to a different note
        // DON'T delete it - it may contain valid work that was saved under the wrong key
        void 0 && console.log(`[Autosave] checkForLocalDraft: Draft noteId mismatch (${draft.noteId} vs ${noteId}) - ignoring (not deleting to prevent data loss)`);
        return null;
      }
      
      // Check if draft is too old (24 hours max - this is reasonable)
      const draftAge = Date.now() - new Date(draft.timestamp).getTime();
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      if (draftAge > maxAge) {
        void 0 && console.log('[Autosave] checkForLocalDraft: Draft too old (>24 hours), clearing');
        clearLocalDraft(true);
        return null;
      }
      
      // TIMESTAMP CHECK: Is the draft significantly newer than the server?
      const serverUpdatedAtValue = serverUpdatedAtRef.current;
      let timeDiffSeconds = 0;
      let draftIsSignificantlyNewer = false;

      // Oct 2026 — EMPTY-SERVER OVERRIDE: when the note on the server has
      // no real user content (wiped / never saved), timestamps are
      // meaningless — the save that emptied the note is NEWER than the
      // draft by definition, which used to discard the draft here and
      // made the empty-note auto-restore unreachable.  An empty note is
      // never preferable to recovered content, so keep the draft.
      const serverIsEmpty = countRealUserFields(formDataRef.current) === 0;
      
      if (serverUpdatedAtValue && draft.timestamp) {
        const serverTime = new Date(serverUpdatedAtValue).getTime();
        const draftTime = new Date(draft.timestamp).getTime();
        timeDiffSeconds = (draftTime - serverTime) / 1000;
        
        void 0 && console.log('[Autosave] checkForLocalDraft: Timestamp comparison:', {
          serverUpdatedAt: serverUpdatedAtValue,
          draftTimestamp: draft.timestamp,
          timeDiffSeconds: timeDiffSeconds.toFixed(1),
          bufferRequired: NETWORK_LATENCY_BUFFER_SECONDS,
          noteId: noteId?.slice(0, 8)
        });
        
        // If draft is OLDER than server, discard it (server has newer data)
        // — unless the server note is EMPTY (see override above).
        if (timeDiffSeconds < 0 && !serverIsEmpty) {
          void 0 && console.log('[Autosave] checkForLocalDraft: Draft is OLDER than server - discarding');
          clearLocalDraft(true);
          return null;
        }
        
        // ANTI-FLICKER: Only consider draft "significantly newer" if beyond the buffer
        // This prevents false positives from network latency during normal saves
        draftIsSignificantlyNewer = serverIsEmpty || timeDiffSeconds >= NETWORK_LATENCY_BUFFER_SECONDS;
        
        if (!draftIsSignificantlyNewer) {
          void 0 && console.log(`[Autosave] checkForLocalDraft: Draft only ${timeDiffSeconds.toFixed(1)}s newer (< ${NETWORK_LATENCY_BUFFER_SECONDS}s buffer) - likely just network latency, discarding`);
          clearLocalDraft(true);
          return null;
        }
      } else {
        // No server timestamp to compare - be safe, keep the draft
        void 0 && console.log('[Autosave] checkForLocalDraft: No server timestamp available - keeping draft to be safe');
        draftIsSignificantlyNewer = true;
      }
      
      // CONTENT CHECK: Does the draft have meaningful differences from server?
      // This is the AUTHORITATIVE check - if content is identical, NEVER show banner
      const serverData = formDataRef.current;
      
      if (serverData && Object.keys(serverData).length > 0) {
        const hasDifferences = hasMeaningfulDifference(serverData, draft.formData);
        
        void 0 && console.log('[Autosave] checkForLocalDraft: Content comparison (AUTHORITATIVE):', {
          hasDifferences,
          timeDiffSeconds: timeDiffSeconds.toFixed(1)
        });
        
        // HASH CONSISTENCY: If content is identical, SUPPRESS banner even if timestamps differ
        // This is critical to prevent flickering - content truth trumps timestamps
        if (!hasDifferences) {
          void 0 && console.log('[Autosave] checkForLocalDraft: Content is IDENTICAL - suppressing banner (hash consistency rule)');
          clearLocalDraft(true);
          return null;
        }
        
        void 0 && console.log('[Autosave] checkForLocalDraft: Draft has meaningful differences from server - WILL SHOW BANNER');
      } else {
        // Server data not loaded yet - don't show banner yet, wait for proper comparison
        void 0 && console.log('[Autosave] checkForLocalDraft: Server data not loaded yet, deferring decision');
        return null; // Return null to defer - the useEffect will retry
      }
      
      // Final decision: Show banner ONLY if draft passes BOTH checks:
      // 1. Significantly newer (10+ seconds, beyond network latency)
      // 2. Has meaningful content differences (hash check passed)
      if (draftIsSignificantlyNewer) {
        draft.source = 'local'; // Mark as local draft
        draft.timeDiffSeconds = timeDiffSeconds; // Include for display
        draft.flushStamp = readBeaconFlushStamp(noteId); // Jun 2026 — beacon flush marker
        void 0 && console.log(`[Autosave] checkForLocalDraft: ✅ Draft valid for note ${noteId.slice(0, 8)} (${timeDiffSeconds.toFixed(1)}s newer with different content)`);
        return draft;
      }
      
      return null;
    } catch (e) {
      console.warn('[Autosave] Failed to check local draft:', e);
      return null;
    }
  }, [noteId, encryptionKey, clearLocalDraft, hasMeaningfulDifference]);
  
  /**
   * Check for server-side draft (for admin/supervisor recovery)
   * This is async and returns a promise
   */
  const checkForServerDraft = useCallback(async () => {
    if (!noteId) return null;
    
    try {
      const token = getAuthToken?.() || localStorage.getItem('token');
      if (!token) return null;
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notes/${noteId}/draft`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      if (!response.ok) return null;
      
      const data = await response.json();
      if (!data.draft || !data.draft.form_data) return null;
      
      // Convert server format to local format
      return {
        noteId: data.draft.note_id,
        formData: data.draft.form_data,
        consentData: data.draft.consent_data,
        linkedGoals: data.draft.linked_goals || [],
        timestamp: data.draft.client_side_timestamp || data.draft.created_at,
        userId: data.draft.user_id,
        userName: data.draft.user_name,
        source: 'server', // Mark as server draft
      };
    } catch (e) {
      console.warn('[Autosave] Failed to check server draft:', e);
      return null;
    }
  }, [noteId, getAuthToken]);
  
  /**
   * Build delta object containing only changed fields
   * This significantly reduces payload size for large forms
   */
  const buildDelta = useCallback((currentData, currentConsent, currentGoals) => {
    const delta = {
      fields: {},      // Changed field values: { fieldId: newValue }
      meta: {},        // Meta changes: consent, goals
    };
    
    const changedFieldIds = Array.from(changedFieldsRef.current);
    
    if (changedFieldIds.length === 0) {
      return null; // No changes
    }
    
    // Extract changed field values from current data
    if (currentData?.fields) {
      currentData.fields.forEach(field => {
        if (changedFieldIds.includes(field.id)) {
          delta.fields[field.id] = field.value;
        }
      });
    }
    
    if (currentData?.sections) {
      currentData.sections.forEach(section => {
        section.fields?.forEach(field => {
          if (changedFieldIds.includes(field.id)) {
            delta.fields[field.id] = field.value;
          }
        });
      });
    }
    
    // Include consent if changed
    if (changedFieldIds.includes('__consent_data__')) {
      delta.meta.consent_data = currentConsent;
    }
    
    // Include goals if changed
    if (changedFieldIds.includes('__linked_goals__')) {
      delta.meta.linked_goals = currentGoals;
    }
    
    return delta;
  }, []);

  /**
   * Lazy Auto-Creation: Create a new note on first autosave if noteId is null
   * Returns the new noteId on success, null on failure
   */
  const lazyCreateNote = useCallback(async (formDataToSave) => {
    // Guard against duplicate creation
    if (isCreatingRef.current) {
      void 0 && console.log('[Autosave] Lazy create already in progress, skipping');
      return null;
    }
    
    // Require createNoteData to be provided
    if (!createNoteData) {
      void 0 && console.log('[Autosave] No createNoteData provided, cannot lazy create');
      return null;
    }
    
    // Validate minimum required data
    const hasClient = createNoteData.client_id || (createNoteData.client_ids?.length > 0);
    if (!hasClient || !createNoteData.provider_id) {
      void 0 && console.log('[Autosave] Missing required client or provider for lazy create');
      return null;
    }
    
    isCreatingRef.current = true;
    setSaveStatus(SaveStatus.SAVING);
    
    try {
      const token = getAuthToken?.() || localStorage.getItem('token');
      if (!token) {
        console.warn('[Autosave] No auth token for lazy create');
        isCreatingRef.current = false;
        return null;
      }
      
      // Build note creation payload
      const notePayload = {
        content: JSON.stringify(formDataToSave),
        provider_id: createNoteData.provider_id,
        template_id: createNoteData.template_id || null,
        title: createNoteData.template_name || null,
        status: 'draft',
        linked_goals: linkedGoalsRef.current || [],
        telehealth_consent: consentDataRef.current?.pos_code ? consentDataRef.current : null,
      };
      
      // Add client info based on group note or single
      if (createNoteData.is_group_note && createNoteData.client_ids?.length > 0) {
        notePayload.client_ids = createNoteData.client_ids;
        notePayload.is_group_note = true;
      } else {
        notePayload.client_id = createNoteData.client_id || createNoteData.client_ids?.[0];
        notePayload.is_group_note = false;
      }
      
      void 0 && console.log('[Autosave] Lazy creating note:', notePayload);
      
      // Jun 2026 — AUTO-RETRY: creation retries twice on transient network
      // failures before surfacing the loud error toast.
      let response;
      for (let attempt = 0; ; attempt++) {
        try {
          if (attempt > 0) {
            await new Promise((r) => setTimeout(r, 2000));
            console.warn(`[Autosave] 🔁 Lazy-create retry ${attempt}/2`);
          }
          response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/notes`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify(notePayload),
            }
          );
          break;
        } catch (netErr) {
          if (attempt >= 2) throw netErr;
        }
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[Autosave] Lazy create failed:', response.status, errorData);
        setSaveStatus(SaveStatus.ERROR);
        // CRITICAL: Show prominent error to user - note was NOT created
        toast.error(
          <div>
            <strong>⚠️ Note could not be saved to server</strong>
            <p className="text-sm mt-1">Your work is cached locally, but you won't be able to sign until this is resolved.</p>
            <p className="text-xs mt-1 text-gray-500">Please try refreshing the page. If the problem persists, contact support.</p>
          </div>,
          { duration: 15000 }
        );
        isCreatingRef.current = false;
        return null;
      }
      
      const newNote = await response.json();
      void 0 && console.log('[Autosave] Lazy created note:', newNote.id);
      
      // Update noteId via callback
      if (setNoteId) {
        setNoteId(newNote.id);
      }
      noteIdRef.current = newNote.id;
      
      // Update browser URL so refresh preserves the note
      // Format: /notes/{noteId} or whatever the current URL pattern is
      try {
        const currentPath = window.location.pathname;
        // Check if we're on a "new note" type path and update to the real note ID
        if (currentPath.includes('/notes/new') || currentPath.includes('/note/new')) {
          const newPath = currentPath.replace(/\/notes?\/new/, `/notes/${newNote.id}`);
          window.history.replaceState({ noteId: newNote.id }, '', newPath);
          void 0 && console.log('[Autosave] Updated URL to:', newPath);
        } else if (!currentPath.includes(newNote.id)) {
          // Add noteId as a query param if not already in URL
          const url = new URL(window.location.href);
          url.searchParams.set('autosave_note_id', newNote.id);
          window.history.replaceState({ noteId: newNote.id }, '', url.toString());
          void 0 && console.log('[Autosave] Added autosave_note_id to URL');
        }
      } catch (urlError) {
        console.warn('[Autosave] Failed to update URL:', urlError);
      }
      
      // Initialize version tracking
      currentVersionRef.current = newNote.current_version || 1;
      
      // Cache locally as well (include consent and goals)
      cacheDraftLocally(formDataToSave, consentDataRef.current, linkedGoalsRef.current);
      
      // Update UI state
      setSaveStatus(SaveStatus.SAVED);
      setLastSavedAt(new Date());
      lastSavedHashRef.current = computeHash(formDataToSave);
      lastSaveTimeRef.current = Date.now();
      lastSavedFormDataRef.current = JSON.parse(JSON.stringify(formDataToSave));
      // Jun 2026 — seed the hydration baseline so the extended hydration
      // gate (which now also covers 'manual' saves) doesn't block the very
      // next save on a freshly lazy-created note.
      if (!initialServerDataRef.current) {
        initialServerDataRef.current = JSON.parse(JSON.stringify(formDataToSave));
      }
      if (onNoteCreated) {
        onNoteCreated(newNote);
      }
      
      isCreatingRef.current = false;
      return newNote.id;
      
    } catch (error) {
      console.error('[Autosave] Lazy create error:', error);
      setSaveStatus(SaveStatus.ERROR);
      // CRITICAL: Show prominent error to user - note was NOT created
      toast.error(
        <div>
          <strong>⚠️ Note could not be saved to server</strong>
          <p className="text-sm mt-1">Your work is cached locally, but you won't be able to sign until this is resolved.</p>
          <p className="text-xs mt-1 text-gray-500">Error: {error.message}</p>
        </div>,
        { duration: 15000 }
      );
      // Cache locally as fallback (include consent and goals)
      cacheDraftLocally(formDataToSave, consentDataRef.current, linkedGoalsRef.current);
      isCreatingRef.current = false;
      return null;
    }
  }, [createNoteData, setNoteId, getAuthToken, onNoteCreated, cacheDraftLocally]);

  /**
   * Silent save to server using DELTA updates
   * Only sends changed fields, not the entire form
   * Returns true if saved, false if skipped
   */
  const silentSave = useCallback(async (trigger = 'autosave', specificFields = []) => {
    void 0 && console.log('[Autosave] silentSave called:', { trigger, enabled, noteId: noteIdRef.current, isInitialized: isInitializedRef.current });
    
    // CRITICAL: Block ALL saves until note is fully initialized
    // This prevents "Version 0" ghost saves that cause immediate conflicts on load
    if (!isInitializedRef.current) {
      void 0 && console.log('[Autosave] ⛔ Blocking save - note not yet initialized');
      return false;
    }
    
    // CRITICAL: Check enabled flag at the START of save
    if (!enabled) {
      void 0 && console.log('[Autosave] Skipping - not enabled');
      return false;
    }
    
    // CRITICAL: Block autosaves during initial load period to prevent false change detection
    // Manual saves and draft auto-restore saves are always allowed
    if (trigger !== 'manual' && trigger !== 'draft_auto_restore' && noteLoadedAtRef.current) {
      const timeSinceLoad = Date.now() - noteLoadedAtRef.current;
      if (timeSinceLoad < INITIAL_LOAD_DELAY_MS) {
        void 0 && console.log(`[Autosave] Skipping - note loaded ${timeSinceLoad}ms ago, waiting ${INITIAL_LOAD_DELAY_MS}ms before autosave`);
        return false;
      }
    }

    // Jun 2026 — FIRST-SAVE HYDRATION GATE (root cause of content-loss blocks
    // on duplicated notes): a save on an EXISTING note must not fire until
    // the editor is fully hydrated and the comparison baseline
    // (initialServerDataRef / lastSavedFormDataRef) is populated.  Extended
    // to 'manual' too: sign flows and close handlers call forceSave, which
    // previously bypassed this gate and shipped an empty-reading payload on
    // a freshly duplicated note, tripping the backend content-preservation
    // guard and surfacing a save error to the provider.
    if (trigger !== 'draft_auto_restore' && noteIdRef.current) {
      if (!initialServerDataRef.current || !lastSavedFormDataRef.current) {
        void 0 && console.log('[Autosave] ⛔ Blocking save - editor not hydrated / baseline not yet established', { trigger });
        return false;
      }
    }
    
    // LAZY CREATION: If no noteId but we have createNoteData, create the note first
    if (!noteIdRef.current && createNoteData) {
      void 0 && console.log('[Autosave] No noteId - triggering lazy creation');
      const currentData = formDataRef.current;
      
      // Only create if there's actual content
      if (!currentData || Object.keys(currentData).length === 0) {
        void 0 && console.log('[Autosave] No content yet, skipping lazy create');
        return false;
      }
      
      // Check if there's meaningful content (not just empty fields)
      const hasContent = currentData.fields?.some(f => f.value && f.value.toString().trim()) ||
                        currentData.sections?.some(s => s.fields?.some(f => f.value && f.value.toString().trim()));
      
      if (!hasContent) {
        void 0 && console.log('[Autosave] No meaningful content yet, skipping lazy create');
        return false;
      }
      
      const newNoteId = await lazyCreateNote(currentData);
      if (!newNoteId) {
        void 0 && console.log('[Autosave] Lazy creation failed');
        return false;
      }
      
      // Note created successfully, subsequent saves will use the new noteId
      void 0 && console.log('[Autosave] Lazy creation successful, noteId:', newNoteId);
      return true;
    }
    
    // Standard save path - require noteId
    if (!noteIdRef.current) {
      void 0 && console.log('[Autosave] Skipping - no noteId and no createNoteData');
      return false;
    }
    
    // If already saving, queue this save instead of skipping
    if (isSavingRef.current) {
      void 0 && console.log('[Autosave] Save in progress, will retry after current save completes');
      pendingSaveRef.current = { trigger, changedFields: specificFields };
      return false;
    }
    
    // CRITICAL: Capture the data IMMEDIATELY at the start of save
    // This prevents reading stale/null data if state is cleared during async operations
    const currentData = formDataRef.current;
    const currentConsent = consentDataRef.current;
    const currentGoals = linkedGoalsRef.current;
    
    // Double-check enabled hasn't changed (race condition protection)
    if (!enabled) {
      return false;
    }
    
    // CRITICAL: Skip saving if content is empty or has no fields
    // This prevents overwriting note content with empty data during initialization
    if (!currentData) {
      void 0 && console.log('[Autosave] Skipping save - formData is null/undefined');
      return false;
    }
    
    // Also skip if formData is empty object {}
    if (typeof currentData === 'object' && Object.keys(currentData).length === 0) {
      void 0 && console.log('[Autosave] Skipping save - formData is empty object {}');
      return false;
    }
    
    const hasFields = (currentData.fields && currentData.fields.length > 0) || 
                      (currentData.sections && currentData.sections.some(s => s.fields && s.fields.length > 0));
    
    if (!hasFields) {
      void 0 && console.log('[Autosave] Skipping save - formData has no fields (likely still initializing)');
      return false;
    }
    
    // CRITICAL: Check if form fields have ANY actual user-entered values
    // Must distinguish between: empty, default/placeholder values, and real user content
    const fieldValues = currentData.fields || [];
    const hasRealContent = fieldValues.some(field => {
      const val = field.value;
      const defaultVal = field.default_value || field.placeholder || '';
      
      // Skip if empty
      if (val === null || val === undefined || val === '') return false;
      if (Array.isArray(val) && val.length === 0) return false;
      if (val === '[]') return false;
      
      // Skip if value equals the default/placeholder (not real user input)
      if (val === defaultVal) return false;
      
      // Skip common placeholder patterns
      if (typeof val === 'string') {
        const lowerVal = val.toLowerCase().trim();
        if (lowerVal.startsWith('enter ') || 
            lowerVal.startsWith('type ') || 
            lowerVal.startsWith('describe ') ||
            lowerVal.includes('your response') ||
            lowerVal === 'n/a' ||
            lowerVal === 'none') {
          return false;
        }
      }
      
      return true;
    });
    
    // If this is NOT a manual save and the form has NO real content, skip it
    // Manual saves are always allowed (user explicitly wants to save)
    if (!hasRealContent && trigger !== 'manual') {
      void 0 && console.log('[Autosave] Skipping autosave - form has fields but no real user content (only defaults/placeholders)');
      return false;
    }
    
    // BASELINE CHECK: Before attempting save, check if there are MEANINGFUL differences
    // from the last saved state. This prevents unnecessary saves and version increments.
    // Only save if a human actually changed a value.
    // Jun 2026 — the baseline compares FORM CONTENT only, so metadata (DOS/
    // times/units), consent and goals edits must bypass this abort or they
    // would be silently dropped (and the dirty flags wiped).
    const baselineData = initialServerDataRef.current || lastSavedFormDataRef.current;
    const hasNonContentChange =
      changedFieldsRef.current.has('__metadata__') ||
      changedFieldsRef.current.has('__consent_data__') ||
      changedFieldsRef.current.has('__linked_goals__');
    
    if (baselineData && trigger !== 'manual' && !hasNonContentChange) {
      const hasMeaningfulDiff = hasMeaningfulDifference(baselineData, currentData);
      
      if (!hasMeaningfulDiff) {
        // ABORT: No meaningful difference from baseline - skip save entirely
        void 0 && console.log('[Autosave] ⏭️ Skipping save - no meaningful difference from baseline');
        
        // Reset state: Clear dirty flags since content matches saved state
        changedFieldsRef.current.clear();
        hasUnsavedChangesRef.current = false;
        
        // Keep status as SAVED or IDLE (don't show "Saving...")
        if (saveStatus !== SaveStatus.SAVED) {
          setSaveStatus(SaveStatus.IDLE);
        }
        
        return 'noop'; // nothing to save — NOT a refusal (flushSave must not beacon)
      }
      
      void 0 && console.log('[Autosave] ✅ Meaningful difference detected from baseline, proceeding with save');
    }
    
    // Create combined state object for hash comparison (secondary check for rapid-fire saves)
    const combinedState = {
      formData: currentData,
      consentData: currentConsent,
      linkedGoals: currentGoals,
    };
    const currentHash = computeHash(combinedState);
    
    // Skip if unchanged (using hash comparison) - catches consent/goals changes too.
    // Jun 2026 — EXCEPT when metadata changed (DOS/times/units): the content
    // hash doesn't cover metadata, so a metadata-only edit must still save.
    if (currentHash === lastSavedHashRef.current && !changedFieldsRef.current.has('__metadata__')) {
      void 0 && console.log('[Autosave] Skipping - hash unchanged');
      changedFieldsRef.current.clear(); // Clear tracked changes
      hasUnsavedChangesRef.current = false;
      return 'noop'; // nothing to save — NOT a refusal (flushSave must not beacon)
    }
    
    void 0 && console.log('[Autosave] Hash changed, proceeding with save. changedFieldsRef size:', changedFieldsRef.current.size);
    
    // Rate limiting — bypassed exactly once for a conflict auto-retry
    // (the preceding successful save was <5s ago by definition).
    // flush_save and manual are FINAL saves and must never be rate-limited
    // (iter514 fix: an edit within 5s of the last autosave was lost on close).
    const now = Date.now();
    const isFinalSave = trigger === 'flush_save' || trigger === 'manual';
    if (!isFinalSave && now - lastSaveTimeRef.current < MIN_SAVE_INTERVAL_MS && !conflictRetryBypassRef.current) {
      return false;
    }
    conflictRetryBypassRef.current = false;
    
    // Build delta of changed fields only
    const delta = buildDelta(currentData, currentConsent, currentGoals);
    const changedFieldIds = Array.from(changedFieldsRef.current);
    
    void 0 && console.log('[Autosave] buildDelta result:', { 
      hasDelta: !!delta, 
      changedFieldIds,
      changedFieldsRefSize: changedFieldsRef.current.size 
    });
    
    // If no delta and this is first save, send full form
    const isFirstSave = !lastSavedFormDataRef.current;
    const useFullSave = isFirstSave || trigger === 'manual' || !delta;
    
    isSavingRef.current = true;
    
    // First, cache locally (in case server call fails) - always cache FULL state
    cacheDraftLocally(currentData, currentConsent, currentGoals);
    
    try {
      // Update status silently (won't cause form re-render)
      setSaveStatus(SaveStatus.SAVING);
      
      const payloadSize = useFullSave 
        ? JSON.stringify(currentData).length 
        : JSON.stringify(delta).length;
      
      void 0 && console.log(`[Autosave] Starting ${trigger} ${useFullSave ? 'FULL' : 'DELTA'} save for note ${noteId} (${(payloadSize/1024).toFixed(1)}KB, ${changedFieldIds.length} fields changed, expecting v${currentVersionRef.current})...`);
      
      // CRITICAL: Skip save if version hasn't been synchronized yet
      // This prevents version conflicts when opening a note written by someone else
      if (currentVersionRef.current === null && trigger !== 'manual') {
        void 0 && console.log('[Autosave] Skipping - version not yet synchronized (waiting for initialVersion)');
        isSavingRef.current = false;
        setSaveStatus(SaveStatus.IDLE); // don't leave "Saving..." stuck
        return false;
      }
      
      const token = getAuthToken?.() || localStorage.getItem('token');
      
      // Construct request body - either full form or delta
      // OPTIMISTIC LOCKING: Include expected_version to prevent lost updates
      const requestBody = useFullSave 
        ? {
            // Full save - send everything (for first save or manual save)
            form_data: currentData,
            consent_data: currentConsent,
            linked_goals: currentGoals,
            metadata: metadataRef.current || null,  // Include metadata (date_of_service, times, etc.)
            save_trigger: trigger,
            session_id: sessionIdRef.current,
            is_delta: false,
            expected_version: currentVersionRef.current,  // Optimistic lock
            client_id: clientIdRef.current || null,  // Cross-contamination prevention
            consent_touched: changedFieldsRef.current.has('__consent_data__'),  // Echo guard
          }
        : {
            // Delta save - send only changes
            delta: delta,
            changed_field_ids: changedFieldIds,
            metadata: changedFieldsRef.current.has('__metadata__') ? metadataRef.current : null,  // Include metadata if changed
            save_trigger: trigger,
            session_id: sessionIdRef.current,
            is_delta: true,
            expected_version: currentVersionRef.current,  // Optimistic lock
            client_id: clientIdRef.current || null,  // Cross-contamination prevention
          };
      // Jun 2026 — snapshot the metadata we are SENDING so the response
      // handler only clears the metadata dirty marker if the user hasn't
      // changed metadata again while this request was in flight.
      const sentMetadataJson = JSON.stringify(requestBody.metadata || null);
      
      // Capture the note ID we intend to save to
      const currentNoteId = noteIdRef.current;
      
      // FINAL SAFETY CHECK: Abort if note is being closed (enabled became false)
      if (!enabled) {
        void 0 && console.log('[Autosave] Aborting before fetch - note is being closed');
        isSavingRef.current = false;
        return false;
      }
      
      // CRITICAL SAFETY CHECK: Verify noteId ref hasn't changed during async operations
      // This prevents cross-note contamination if user rapidly switches notes
      if (noteIdRef.current !== currentNoteId) {
        console.warn('[Autosave] ⚠️ Note ID changed during save! Aborting to prevent contamination.', {
          startedWith: currentNoteId?.slice(0, 8),
          nowIs: noteIdRef.current?.slice(0, 8)
        });
        isSavingRef.current = false;
        return false;
      }
      
      // Jun 2026 — AUTO-RETRY: transient network failures/timeouts retry up
      // to 2 more times (2s apart) before surfacing an error.  Each attempt
      // gets its own 30s AbortController.  Retries abort if the note was
      // closed or switched while waiting.
      const attemptFetch = async () => {
        const controller = new AbortController();
        const tid = setTimeout(() => controller.abort(), 30000);
        try {
          return await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/notes/${currentNoteId}/autosave`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify(requestBody),
              signal: controller.signal,
            }
          );
        } finally {
          clearTimeout(tid);
        }
      };
      
      let response;
      for (let attempt = 0; ; attempt++) {
        try {
          if (attempt > 0) {
            await new Promise((r) => setTimeout(r, 2000));
            if (!enabled || noteIdRef.current !== currentNoteId) {
              throw new Error('Note closed during save retry');
            }
            console.warn(`[Autosave] 🔁 Retry ${attempt}/2 for note ${currentNoteId?.slice(0, 8)}`);
          }
          response = await attemptFetch();
          break;
        } catch (netErr) {
          if (attempt >= 2 || !enabled || noteIdRef.current !== currentNoteId) {
            throw netErr;
          }
        }
      }
      
      // Handle 401 Unauthorized - Session expired
      if (response.status === 401) {
        console.error('[Autosave] ❌ Session expired (401) - user needs to re-authenticate');
        setSaveStatus(SaveStatus.SESSION_EXPIRED);
        
        // Dispatch custom event for App.js to show session expired modal
        window.dispatchEvent(new CustomEvent('session-expired', { 
          detail: { 
            reason: 'autosave_401',
            noteId: currentNoteId,
            hasCachedData: true 
          }
        }));
        
        return false;
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[Autosave] Server returned ${response.status}:`, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      
      if (!data.skipped) {
        // Success - update tracking
        lastSavedHashRef.current = currentHash;
        lastSaveTimeRef.current = now;
        setLastSavedAt(new Date());  // Pass Date object for SaveIndicator component
        setSaveStatus(SaveStatus.SAVED);
        
        // IMPORTANT: Update lastSavedFormData snapshot for future delta comparisons
        lastSavedFormDataRef.current = JSON.parse(JSON.stringify(currentData));
        
        // CRITICAL FIX: Update initialServerDataRef to the saved data
        // This ensures hasRealUnsavedChanges() returns false after successful save
        // Without this, the exit guard compares against stale initial data
        initialServerDataRef.current = JSON.parse(JSON.stringify(currentData));
        initialServerHashRef.current = currentHash;  // perf — we just computed it, reuse
        void 0 && console.log('[Autosave] 📸 Updated baseline after successful save');
        
        // Clear changed fields tracker and mark as saved
        changedFieldsRef.current.clear();
        hasUnsavedChangesRef.current = false;
        
        // SAVE LOOP PREVENTION: Set justSaved flag to block immediate re-detection
        // This prevents the formData effect from re-adding to changedFieldsRef
        justSavedRef.current = true;
        setTimeout(() => {
          justSavedRef.current = false;
        }, 3000); // Block change detection for 3 seconds after save
        
        // OPTIMISTIC LOCKING: Update our version to match what was saved
        if (data.version) {
          currentVersionRef.current = data.version;
        }
        
        // SYNC TIMESTAMP: Update serverUpdatedAtRef to match the server's response
        // This "levels the scale" so recovery logic sees timestamps are in sync
        if (data.updated_at) {
          serverUpdatedAtRef.current = data.updated_at;
          void 0 && console.log(`[Autosave] 🕐 Synced in-memory timestamp to server: ${data.updated_at}`);
          
          // POST-SAVE SYNC: Also update localStorage timestamp BEFORE clearing
          // This handles the race condition: if user refreshes after save succeeds but 
          // before clearLocalDraft() executes, the localStorage draft will have matching
          // timestamp and the banner won't incorrectly appear
          try {
            const storageKey = getStorageKey(noteId);
            const encrypted = localStorage.getItem(storageKey);
            if (encrypted) {
              const draft = decryptData(encrypted, encryptionKey);
              if (draft) {
                // Update the draft timestamp to match server's updated_at
                draft.timestamp = data.updated_at;
                const reEncrypted = encryptData(draft, encryptionKey);
                if (reEncrypted) {
                  localStorage.setItem(storageKey, reEncrypted);
                  void 0 && console.log(`[Autosave] 🔄 Synced localStorage draft timestamp to: ${data.updated_at}`);
                }
              }
            }
          } catch (e) {
            // Non-critical - proceed to clear
            console.debug('[Autosave] Could not sync localStorage timestamp:', e.message);
          }
        }
        
        void 0 && console.log(`[Autosave] ✅ Successfully saved note ${noteId} (version: ${data.version || 'unknown'}, mode: ${useFullSave ? 'full' : 'delta'})`);
        
        // Clear local cache since server has the data and timestamps are synced.
        // Oct 2026 EXCEPTION: if what we just saved carries NO real user
        // content (e.g. the template-initialized materialization of an
        // empty/wiped note) while the on-disk draft DOES, keep the draft —
        // clearing here would destroy the only recoverable copy before the
        // empty-note auto-restore check gets a chance to run.
        try {
          const savedWasEmpty = countRealUserFields(formDataRef.current) === 0;
          let draftHasRealContent = false;
          if (savedWasEmpty) {
            const enc = localStorage.getItem(getStorageKey(noteId));
            const d = enc ? decryptData(enc, encryptionKey) : null;
            draftHasRealContent = !!d?.formData && countRealUserFields(d.formData) > 0;
          }
          if (savedWasEmpty && draftHasRealContent) {
            void 0 && console.log('[Autosave] ⚠️ Saved content is empty but local draft has real content — keeping draft for recovery');
          } else {
            clearLocalDraft();
          }
        } catch {
          clearLocalDraft();
        }
        
        // Clear any previous conflict state
        setVersionConflict(null);
        conflictRetryCountRef.current = 0;
        
        // CLEAR any previous error states on successful save
        if (onSaveError) {
          onSaveError(null);
        }
        if (onSaveCriticalError) {
          onSaveCriticalError(null);
        }
        
        return true;
      } else if (data.conflict) {
        // OPTIMISTIC LOCKING: Version conflict detected!
        console.error(
          `[Autosave] ⚠️ VERSION CONFLICT for note ${noteId}: ` +
          `our version ${currentVersionRef.current}, server has ${data.current_version}`
        );

        // Jun 2026 — CONFLICT AUTO-RETRY: slow overlapping saves from this
        // SAME session (e.g. a queued blur save racing a 5s autosave) bump
        // the version mid-flight.  If the local form still preserves every
        // user-content value on the server, silently resync the version and
        // retry instead of surfacing the conflict/recovery UI.  A genuine
        // concurrent edit (server has content we'd clobber) still falls
        // through to the existing conflict handling.
        if (trigger !== 'manual' && conflictRetryCountRef.current < 2) {
          try {
            const resp = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/notes/${noteId}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (resp.ok) {
              const serverNote = await resp.json();
              let serverFd = {};
              try {
                serverFd = typeof serverNote.content === 'string'
                  ? JSON.parse(serverNote.content || '{}')
                  : (serverNote.content || {});
              } catch { serverFd = {}; }
              // Safe to retry when EITHER:
              //  a) the server content matches our last-known baseline — the
              //     version bump carried nothing we don't already have
              //     (self-inflicted race or a content-neutral save), OR
              //  b) our local form preserves every user-content value on the
              //     server (pure additions).
              const baselineFd = lastSavedFormDataRef.current || initialServerDataRef.current;
              const serverMatchesBaseline = !!baselineFd && !hasMeaningfulDifference(serverFd, baselineFd);
              if (serverMatchesBaseline || localPreservesServerContent(serverFd, formDataRef.current)) {
                conflictRetryCountRef.current += 1;
                currentVersionRef.current = serverNote.current_version ?? data.current_version;
                serverUpdatedAtRef.current = serverNote.updated_at || serverUpdatedAtRef.current;
                console.warn(
                  `[Autosave] 🔁 Conflict auto-resync to v${currentVersionRef.current} — ` +
                  `retrying ${trigger} save (attempt ${conflictRetryCountRef.current}/2)`
                );
                conflictRetryBypassRef.current = true;
                setTimeout(() => {
                  if (silentSaveRef.current) silentSaveRef.current('conflict_retry', specificFields);
                }, 400);
                return false;
              }
              void 0 && console.log('[Autosave] Conflict auto-retry declined — server has content the local form would clobber');
            }
          } catch { /* resync fetch failed — fall through to conflict UI */ }
        }

        setSaveStatus(SaveStatus.CONFLICT);
        setVersionConflict({
          localVersion: currentVersionRef.current,
          serverVersion: data.current_version,
        });
        
        // Notify the parent component about the conflict
        if (onVersionConflict) {
          onVersionConflict({
            noteId,
            localVersion: currentVersionRef.current,
            serverVersion: data.current_version,
            localData: currentData,
          });
        }
        
        return false;
      } else if (data.catastrophic_loss) {
        // CATASTROPHIC LOSS PROTECTION: Backend blocked save due to >25% content loss
        // This is a safety net against frontend bugs
        console.error(
          `[Autosave] 🛑 CATASTROPHIC LOSS PREVENTED for note ${noteId}: ` +
          `would have lost ${data.content_loss_percent}% of content`
        );
        
        setSaveStatus(SaveStatus.ERROR);
        
        // LOUD FAILURE: This is critical - show error banner
        if (onSaveError) {
          onSaveError('Content Loss Detected - Save Blocked');
        }
        if (onSaveCriticalError) {
          onSaveCriticalError(`Save was blocked because this change removes ${data.content_loss_percent}% of the note's saved content. Your full note is still in the form. If this large change is intentional, click "Save Blocked — Review" to save anyway.`);
        }
        
        // Don't clear local draft - keep the data safe locally
        // The user's current edits are preserved in the form
        
        // Dispatch event for App.js to show warning
        window.dispatchEvent(new CustomEvent('autosave-catastrophic-loss', {
          detail: {
            noteId,
            lossPercent: data.content_loss_percent,
          }
        }));
        
        return false;
      } else {
        // Server skipped the save. Jun 2026: skipped/rejected saves must NOT
        // report "Saved" — guard rejections mean the server refused the data.
        const reason = data.reason || 'unchanged';
        const BENIGN_SKIPS = ['unchanged', 'debounced', 'no_changes', 'debounced_or_unchanged', 'metadata_saved'];
        if (data.skipped && !BENIGN_SKIPS.includes(reason)) {
          console.warn(`[Autosave] ⚠️ Save SKIPPED by server (${reason}) — content was NOT saved`);
          setSaveStatus(SaveStatus.ERROR);
          if (onSaveError) {
            onSaveError(`Not saved — ${reason.replace(/_/g, ' ')}. Your work is kept locally.`);
          }
          // Do NOT clear the local draft — keeping it lets the recovery
          // banner surface on reload if the server never accepts the save.
          return false;
        }
        void 0 && console.log(`[Autosave] Server skipped save (${reason})`);
        // Jun 2026 — 'metadata_saved' means the server APPLIED the metadata
        // (DOS/times/units) even though no content version was written.
        // Clear the metadata dirty marker or the interval re-sends the
        // identical save every 5s forever.
        if (reason === 'metadata_saved' &&
            JSON.stringify(metadataRef.current || null) === sentMetadataJson) {
          changedFieldsRef.current.delete('__metadata__');
          if (changedFieldsRef.current.size === 0) hasUnsavedChangesRef.current = false;
        }
        setSaveStatus(SaveStatus.SAVED);
        return false;
      }
    } catch (error) {
      let errorMessage;
      if (error.name === 'AbortError') {
        errorMessage = 'Save timed out (retried automatically). Your work is cached locally.';
        console.error('[Autosave] ❌ Request timed out after automatic retries');
      } else {
        errorMessage = `Save failed: ${error.message}. Your work is cached locally.`;
        console.error('[Autosave] ❌ Server save FAILED after retries, data cached locally:', error.message);
      }
      
      // LOUD FAILURE: Notify parent component about the error
      if (onSaveError) {
        onSaveError('Save Failed - Please Try Again');
      }
      if (onSaveCriticalError && trigger === 'manual') {
        // Only show critical error banner for manual saves (user clicked button)
        onSaveCriticalError(`Your note could not be saved: ${error.message}. Your work is cached locally, but please copy your content to be safe.`);
      }
      
      // Keep local cache as backup
      setSaveStatus(SaveStatus.OFFLINE_CACHED);
      
      return false;
    } finally {
      isSavingRef.current = false;
      
      // Process any pending save that was queued while we were saving
      if (pendingSaveRef.current) {
        const pending = pendingSaveRef.current;
        pendingSaveRef.current = null;
        void 0 && console.log('[Autosave] Processing queued save...');
        // Use setTimeout to avoid stack overflow on rapid saves
        setTimeout(() => silentSave(pending.trigger, pending.changedFields), 100);
      }
    }
  }, [enabled, noteId, getAuthToken, cacheDraftLocally, clearLocalDraft, hasMeaningfulDifference, saveStatus]);
  
  // Keep silentSave ref updated to avoid stale closure issues in debounce effect
  useEffect(() => {
    silentSaveRef.current = silentSave;
  }, [silentSave]);
  
  // AUTOSAVE: Simple interval-based save
  // Checks for meaningful changes and saves when:
  // 1. There are actual differences from baseline
  // 2. Not already saving
  // 3. Enough time has passed since last save
  useEffect(() => {
    if (!enabled || !noteIdRef.current) {
      return;
    }
    
    const checkAndSave = () => {
      // Skip if just saved (recently completed a save)
      if (justSavedRef.current) {
        return;
      }
      
      // Skip if already saving
      if (isSavingRef.current) {
        return;
      }
      
      // Skip if not initialized
      if (!isInitializedRef.current) {
        return;
      }
      
      // Skip if no form data
      if (!formDataRef.current) {
        return;
      }
      
      // Check if enough time has passed since last save (minimum 10 seconds)
      const timeSinceLastSave = Date.now() - lastSaveTimeRef.current;
      if (timeSinceLastSave < 10000) {
        return;
      }
      
      // Only save if there are actual changes from baseline.
      // Jun 2026 — FIRST-SAVE HYDRATION GATE: the baseline MUST exist before
      // any dirty-triggered save fires.  Previously a missing baseline fell
      // through to an unconditional save — on a freshly opened/duplicated
      // note that shipped a partially hydrated payload and tripped the
      // backend content-loss block.
      const baselineData = initialServerDataRef.current || lastSavedFormDataRef.current;
      if (!baselineData) {
        void 0 && console.log('[Autosave] Interval check skipped - baseline not yet established');
        return;
      }
      // Quick hash check first (metadata-only edits bypass — hash doesn't cover them)
      const currentHash = computeHash({ formData: formDataRef.current, consentData: consentDataRef.current, linkedGoals: linkedGoalsRef.current });
      if (currentHash === lastSavedHashRef.current && !changedFieldsRef.current.has('__metadata__')) {
        return; // No changes
      }
      
      void 0 && console.log('[Autosave] Interval check - triggering save');
      if (silentSaveRef.current) {
        silentSaveRef.current('autosave');
      }
    };
    
    // Check every 5 seconds, but only save if conditions are met
    const interval = setInterval(checkAndSave, 5000);
    
    return () => {
      clearInterval(interval);
    };
  }, [enabled, noteId]); // Removed saveStatus - interval should run regardless
  
  /**
   * Clean up old drafts from localStorage (runs on mount)
   * Removes any draft older than 24 hours to prevent localStorage from filling up
   */
  const cleanupOldDrafts = useCallback(() => {
    try {
      const keysToRemove = [];
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      const now = Date.now();
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        // FIXED: Use correct prefix (emr_draft_) instead of wrong one (note_draft_)
        if (key && key.startsWith(STORAGE_PREFIX)) {
          try {
            const encrypted = localStorage.getItem(key);
            if (encrypted) {
              const draft = decryptData(encrypted, encryptionKey);
              if (draft && draft.timestamp) {
                const draftAge = now - new Date(draft.timestamp).getTime();
                if (draftAge > maxAge) {
                  keysToRemove.push(key);
                }
              }
            }
          } catch (e) {
            // If we can't decrypt it, it's probably corrupted - remove it
            keysToRemove.push(key);
          }
        }
      }
      
      // Remove old drafts
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        void 0 && console.log(`[Autosave] Cleaned up old draft: ${key}`);
      });
      
      if (keysToRemove.length > 0) {
        void 0 && console.log(`[Autosave] Cleaned up ${keysToRemove.length} old draft(s) from localStorage`);
      }
    } catch (e) {
      console.warn('[Autosave] Failed to cleanup old drafts:', e);
    }
  }, [encryptionKey]);
  
  // Run cleanup on mount and periodically (every 10 minutes) to prevent localStorage bloat
  useEffect(() => {
    cleanupOldDrafts();
    const interval = setInterval(cleanupOldDrafts, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [cleanupOldDrafts]);
  
  /**
   * Field blur handler with debouncing
   * Use this on text fields: onBlur={() => onFieldBlur('fieldName')}
   */
  const onFieldBlur = useCallback((fieldName) => {
    if (!enabled) return;
    
    // SAVE LOOP PREVENTION: Skip if we just saved
    if (justSavedRef.current) {
      void 0 && console.log('[Autosave] onFieldBlur skipped - just saved');
      return;
    }
    
    // Skip if no unsaved changes
    if (!hasUnsavedChangesRef.current) {
      void 0 && console.log('[Autosave] onFieldBlur skipped - no unsaved changes');
      return;
    }
    
    // Clear any pending blur save
    if (fieldBlurTimeoutRef.current) {
      clearTimeout(fieldBlurTimeoutRef.current);
    }
    
    // Debounce the save - use longer delay to avoid rapid saves
    fieldBlurTimeoutRef.current = setTimeout(() => {
      // Double check before saving
      if (justSavedRef.current || !hasUnsavedChangesRef.current) {
        void 0 && console.log('[Autosave] onFieldBlur save cancelled - conditions changed');
        return;
      }
      silentSave('field_blur', fieldName ? [fieldName] : []);
    }, 1000); // Increased from FIELD_BLUR_DEBOUNCE_MS to 1 second
  }, [enabled, silentSave]);
  
  /**
   * Workstream C3 hardening — flush any pending debounced local-cache
   * write immediately.  Called by forceSave / window beforeunload /
   * unmount cleanup so data is never lost in the 750ms typing-pause
   * gap.  No-op if no write is pending.
   */
  const flushPendingCacheWrite = useCallback(() => {
    if (!cacheWriteTimeoutRef.current) return;
    clearTimeout(cacheWriteTimeoutRef.current);
    cacheWriteTimeoutRef.current = null;
    if (!isDirtyForCacheRef.current) return;
    const latestFormData = formDataRef.current;
    if (!latestFormData || !noteIdRef.current) {
      isDirtyForCacheRef.current = false;
      return;
    }
    try {
      const token = getAuthToken?.() || localStorage.getItem('token');
      const key = deriveEncryptionKey(token);
      const draftData = {
        noteId: noteIdRef.current,
        formData: latestFormData,
        consentData: consentDataRef.current || null,
        linkedGoals: linkedGoalsRef.current || [],
        timestamp: new Date().toISOString(),
        sessionId: sessionIdRef.current,
      };
      const encrypted = encryptData(draftData, key);
      if (encrypted) {
        safeSetDraft(noteIdRef.current, encrypted);
      }
    } catch (e) {
      // Silent fail
    }
    isDirtyForCacheRef.current = false;
  }, [getAuthToken]);

  /**
   * Force save (for manual save button)
   */
  const forceSave = useCallback(async () => {
    // CRITICAL: Block manual saves until note is initialized
    // This prevents user clicking save before note data is fully loaded
    if (!isInitializedRef.current) {
      void 0 && console.log('[Autosave] ⛔ Blocking manual save - note not yet initialized');
      return false;
    }

    // Flush any pending debounced cache write FIRST so a force-save
    // that races a still-pending typing-pause cache write doesn't
    // overwrite our just-saved server state with older local cache.
    flushPendingCacheWrite();

    // Clear debounce timeout
    if (fieldBlurTimeoutRef.current) {
      clearTimeout(fieldBlurTimeoutRef.current);
    }

    // Force save by temporarily resetting the hash
    lastSavedHashRef.current = null;
    return silentSave('manual');
  }, [silentSave, flushPendingCacheWrite]);
  
  /**
   * Flush save - captures current state and saves immediately
   * Used when closing notes via internal navigation (beforeunload doesn't fire)
   * CRITICAL: Captures refs BEFORE clearing timer to avoid empty state
   */
  const flushSave = useCallback(async () => {
    // Capture current state FIRST before any cleanup
    const capturedData = formDataRef.current;
    const capturedConsent = consentDataRef.current;
    const capturedGoals = linkedGoalsRef.current;
    const capturedNoteId = noteIdRef.current;
    const capturedVersion = currentVersionRef.current;
    const capturedSessionId = sessionIdRef.current;
    
    void 0 && console.log('[Autosave] 🚿 Flush save initiated', {
      hasData: !!capturedData,
      noteId: capturedNoteId?.slice(0, 8),
      hasPendingTimer: !!fieldBlurTimeoutRef.current
    });
    
    // Clear any pending debounce timer
    if (fieldBlurTimeoutRef.current) {
      clearTimeout(fieldBlurTimeoutRef.current);
      fieldBlurTimeoutRef.current = null;
    }
    
    // Skip if no data or note
    if (!capturedData || !capturedNoteId) {
      void 0 && console.log('[Autosave] Flush save skipped - no data or note');
      return false;
    }
    
    // Skip if not initialized
    if (!isInitializedRef.current) {
      void 0 && console.log('[Autosave] Flush save skipped - not initialized');
      return false;
    }
    
    // Check if there are actual changes worth saving — MUST use the same
    // combined shape as lastSavedHashRef ({formData, consentData, linkedGoals})
    // or the dedupe never matches (iter514 fix).
    const currentHash = computeHash({
      formData: capturedData,
      consentData: capturedConsent,
      linkedGoals: capturedGoals,
    });
    if (lastSavedHashRef.current && currentHash === lastSavedHashRef.current && !changedFieldsRef.current.has('__metadata__')) {
      void 0 && console.log('[Autosave] Flush save skipped - no changes from last save');
      return true; // Already saved
    }
    // Never-saved note (no lastSavedHash yet): dedupe against the initial
    // server copy so a view-only close of a brand-new note sends nothing.
    // Uses hasMeaningfulDifference (not a raw hash) because template defaults
    // are applied to formData AFTER the baseline capture (iter517).
    if (!lastSavedHashRef.current && initialServerDataRef.current) {
      if (!hasMeaningfulDifference(initialServerDataRef.current, capturedData) && !changedFieldsRef.current.has('__metadata__')) {
        void 0 && console.log('[Autosave] Flush save skipped - no meaningful change from server copy');
        return true;
      }
    }
    
    // 1. Cache to localStorage immediately (safety net)
    try {
      const token = getAuthToken?.() || localStorage.getItem('token');
      const key = deriveEncryptionKey(token);
      const draftData = {
        noteId: capturedNoteId,
        formData: capturedData,
        consentData: capturedConsent || null,
        linkedGoals: capturedGoals || [],
        timestamp: new Date().toISOString(),
        sessionId: capturedSessionId,
      };
      const encrypted = encryptData(draftData, key);
      if (encrypted) {
        safeSetDraft(capturedNoteId, encrypted);
        void 0 && console.log('[Autosave] ✅ Flush: Cached to localStorage');
      }
    } catch (e) {
      console.warn('[Autosave] Flush: localStorage cache failed:', e);
    }
    
    // 2. Try to save to server using silentSave
    try {
      const result = await silentSave('flush_save');
      // silentSave returns false on silent early-returns (save in flight,
      // init-window gate). Content DIFFERS from the last saved hash here,
      // so a false means the edit would be lost — use the beacon (iter514 fix).
      if (result === false) throw new Error('silent_save_refused');
      void 0 && console.log('[Autosave] ✅ Flush: Server save result:', result);
      return result;
    } catch (e) {
      console.warn('[Autosave] Flush: Server save failed, using sendBeacon as fallback:', e);
      
      // 3. Fallback to sendBeacon if silentSave fails
      try {
        const token = getAuthToken?.() || localStorage.getItem('token');
        if (token) {
          const beaconPayload = JSON.stringify({
            form_data: capturedData,
            consent_data: capturedConsent,
            linked_goals: capturedGoals,
            consent_touched: changedFieldsRef.current.has('__consent_data__'),
            save_trigger: 'flush_save_beacon',
            session_id: capturedSessionId,
            is_delta: false,
            expected_version: capturedVersion,
            auth_token: token,
          });
          const blob = new Blob([beaconPayload], { type: 'application/json' });
          const queued = navigator.sendBeacon(
            `${process.env.REACT_APP_BACKEND_URL}/api/notes/${capturedNoteId}/beacon-save`,
            blob
          );
          if (queued) stampBeaconFlush(capturedNoteId, serverUpdatedAtRef.current);
          void 0 && console.log('[Autosave] ✅ Flush: Beacon sent as fallback');
        }
      } catch (beaconErr) {
        console.warn('[Autosave] Flush: Beacon fallback also failed:', beaconErr);
      }
      return false;
    }
  }, [silentSave, getAuthToken]);

  /**
   * Restore from local draft
   * Returns true if restored, false if failed
   */
  const restoreFromDraft = useCallback(() => {
    if (recoveredDraft && onFormDataRestore) {
      // Restore FULL form state: content, consent, and goals
      onFormDataRestore({
        formData: recoveredDraft.formData,
        consentData: recoveredDraft.consentData || null,
        linkedGoals: recoveredDraft.linkedGoals || [],
      });
      // Read-only mode cannot persist the restore — keep the local draft so
      // it survives until the note is opened editable.
      if (enabledRef.current) clearLocalDraft();
      setShowDraftRecovery(false);
      setRecoveredDraft(null);
      
      // Update hash to combined state to prevent immediate re-save
      const combinedState = {
        formData: recoveredDraft.formData,
        consentData: recoveredDraft.consentData,
        linkedGoals: recoveredDraft.linkedGoals,
      };
      lastSavedHashRef.current = computeHash(combinedState);
      
      return true; // Indicates successful restore
    }
    return false;
  }, [recoveredDraft, onFormDataRestore, clearLocalDraft]);

  /**
   * Jun 2026 — SILENT AUTO-RESTORE: apply a recovered draft without a
   * prompt, then immediately persist it to the server.  The local draft is
   * NOT cleared here — silentSave clears it after the server confirms, so
   * a failed follow-up save keeps the draft recoverable.
   */
  const applySilentRestore = useCallback((draft) => {
    if (!onFormDataRestoreRef.current || !draft?.formData) return false;
    void 0 && console.log('[Autosave] 🔄 Silent auto-restore — applying draft and saving to server');
    onFormDataRestoreRef.current({
      formData: draft.formData,
      consentData: draft.consentData || null,
      linkedGoals: draft.linkedGoals || [],
      source: 'silent_auto_restore',
    });
    setShowDraftRecovery(false);
    setRecoveredDraft(null);
    // Read-only open (e.g. billing ?mode=view): show the recovered content
    // but do NOT write back — skip the follow-up save and KEEP the local
    // draft so it is still recoverable when the note is opened for editing.
    if (!enabledRef.current) {
      void 0 && console.log('[Autosave] Silent restore in read-only mode — draft kept, no save');
      return true;
    }
    lastSavedHashRef.current = null; // force the follow-up save through
    setSaveStatus(SaveStatus.SAVING);
    setTimeout(() => {
      if (silentSaveRef.current) silentSaveRef.current('draft_auto_restore');
    }, 800);
    return true;
  }, []);

  // Jun 2026 — VERSION RESTORE BRIDGE (user report: restoring a previous
  // version left the open editor showing stale content, and saving then
  // overwrote the restore).  NoteHistoryPanel dispatches this event after a
  // successful restore; we load the restored content into the editor and
  // sync version/hash refs so the next save works on top of the restore.
  useEffect(() => {
    const handler = (e) => {
      const d = e.detail || {};
      if (!noteId || d.noteId !== noteId) return;
      void 0 && console.log(`[Autosave] 🔁 Version restored for note ${noteId} — syncing editor`, d.newVersion);
      if (d.newVersion) {
        currentVersionRef.current = d.newVersion;
      }
      serverUpdatedAtRef.current = d.updatedAt || new Date().toISOString();
      if (d.formData && onFormDataRestore) {
        onFormDataRestore({
          formData: d.formData,
          consentData: consentDataRef.current,
          linkedGoals: linkedGoalsRef.current || [],
          source: 'version_restore',
        });
        lastSavedHashRef.current = computeHash({
          formData: d.formData,
          consentData: consentDataRef.current,
          linkedGoals: linkedGoalsRef.current,
        });
      }
      // Server now holds the restored content — drop any stale local draft
      // so the recovery banner can't offer pre-restore content.
      clearLocalDraft();
    };
    window.addEventListener('note-version-restored', handler);
    return () => window.removeEventListener('note-version-restored', handler);
  }, [noteId, onFormDataRestore, clearLocalDraft]);
  
  /**
   * Dismiss draft recovery (use server version)
   */
  const dismissDraftRecovery = useCallback(() => {
    clearLocalDraft();
    setShowDraftRecovery(false);
    setRecoveredDraft(null);
    
    // Update hash to current combined form state
    const combinedState = {
      formData: formDataRef.current,
      consentData: consentDataRef.current,
      linkedGoals: linkedGoalsRef.current,
    };
    lastSavedHashRef.current = computeHash(combinedState);
    
    // Initialize lastSavedFormData for delta tracking
    lastSavedFormDataRef.current = JSON.parse(JSON.stringify(formDataRef.current));
    changedFieldsRef.current.clear();
    hasUnsavedChangesRef.current = false;
  }, [clearLocalDraft]);
  
  // Initialize lastSavedFormData on first render with valid formData
  useEffect(() => {
    if (formData && !lastSavedFormDataRef.current) {
      lastSavedFormDataRef.current = JSON.parse(JSON.stringify(formData));
      void 0 && console.log('[Autosave] Initialized lastSavedFormData snapshot for delta tracking');
    }
  }, [formData]);
  
  // Interval-based autosave - DISABLED
  // The debounced save (triggered on formData changes) handles autosave.
  // Having BOTH causes save loops because:
  // 1. Debounced save fires after user edits
  // 2. Interval save fires 5 seconds later
  // 3. Both detect "changes" and try to save
  // 
  // KEEPING THIS COMMENTED for reference - can re-enable if debounced save is insufficient
  /*
  useEffect(() => {
    const canAutoSave = enabled && (noteId || createNoteData);
    
    if (!canAutoSave) {
      return;
    }
    
    void 0 && console.log('[Autosave] ✅ Starting autosave interval (every', AUTOSAVE_INTERVAL_MS, 'ms)');
    
    const interval = setInterval(() => {
      silentSave('autosave');
    }, AUTOSAVE_INTERVAL_MS);
    
    return () => clearInterval(interval);
  }, [enabled, noteId, createNoteData, silentSave]);
  */
  
  // Check for local AND server drafts on mount - INITIAL LOAD ONLY
  // CRITICAL: This effect ONLY runs when noteId changes (initial mount for each note)
  // The checkForLocalDraft() function handles all the logic (timestamp buffer, hash consistency)
  useEffect(() => {
    // Immediately clear any existing banner when noteId changes
    // This prevents showing stale banners from previous notes
    setShowDraftRecovery(false);
    setRecoveredDraft(null);
    
    // NOTE: intentionally NOT gated on `enabled` — recovery must run even
    // when the note is opened read-only (billing tab's ?mode=view).  Saves
    // remain gated on edit mode inside silentSave/blur/unload paths.
    if (!noteId) {
      setDraftRestorePending(false);
      void 0 && console.log('[Autosave] Draft check skipped - no noteId');
      return;
    }

    // Hold the note's loading state only when a draft CANDIDATE exists in
    // localStorage (cheap raw-presence peek — full validation happens below).
    // Notes without a draft render immediately as before.
    let hasCandidate = false;
    try {
      hasCandidate = !!window.localStorage.getItem(getStorageKey(noteId));
    } catch { hasCandidate = false; }
    setDraftRestorePending(hasCandidate);
    
    void 0 && console.log(`[Autosave] 🔍 Starting INITIAL draft check for note: ${noteId.slice(0, 8)}...`);
    
    // Delay to ensure form data is fully loaded from server before comparison
    const timeoutId = setTimeout(async () => {
      try {
      // CRITICAL: Double-check noteId hasn't changed during the delay
      if (noteIdRef.current !== noteId) {
        void 0 && console.log('[Autosave] Draft check aborted - noteId changed during delay');
        return;
      }
      
      // Check localStorage first (checkForLocalDraft handles all validation logic:
      // - 10-second timestamp buffer for anti-flicker
      // - Hash consistency check (content comparison is authoritative)
      // - Session ID is ignored
      void 0 && console.log(`[Autosave] Checking localStorage for key: ${getStorageKey(noteId)}`);
      let draft = checkForLocalDraft();
      
      if (draft) {
        void 0 && console.log(`[Autosave] ✅ Local draft passed all checks for note ${noteId.slice(0, 8)}:`, {
          draftNoteId: draft.noteId?.slice(0, 8),
          timestamp: draft.timestamp,
          timeDiffSeconds: draft.timeDiffSeconds,
          clientName: draft.clientName
        });
      } else {
        void 0 && console.log(`[Autosave] No valid local draft for note ${noteId.slice(0, 8)}`);
      }
      
      // If no local draft, check server (for admin/supervisor recovery)
      if (!draft) {
        try {
          draft = await checkForServerDraft();
          if (draft && draft.source === 'server') {
            void 0 && console.log(`[Autosave] Found server draft from ${draft.userName || 'another user'}`);
            
            // For server drafts, still do a content comparison
            const serverData = formDataRef.current;
            if (serverData && Object.keys(serverData).length > 0) {
              const hasDifference = hasMeaningfulDifference(serverData, draft.formData);
              if (!hasDifference) {
                void 0 && console.log('[Autosave] Server draft matches current content - discarding');
                draft = null;
              }
            }
          }
        } catch (e) {
          // Server check failed, continue without server draft
        }
      }
      
      // Show banner if we have a valid draft (all checks passed in checkForLocalDraft)
      if (draft) {
        // FINAL CHECK: Ensure draft is still for the current note
        if (noteIdRef.current !== noteId) {
          void 0 && console.log('[Autosave] Draft check aborted before banner - noteId changed');
          return;
        }

        // Jun 2026 — EMPTY-NOTE AUTO-RESTORE: when the note on the server is
        // empty but the draft has real content, silently apply the draft —
        // an empty note is never preferable to recovered content.  The one
        // exception: the user EXPLICITLY saved the note empty (manual save
        // stamps empty_content_manually_saved) — then we prompt as usual so
        // their choice is respected.
        try {
          const currentHasContent = countRealUserFields(formDataRef.current) > 0;
          const draftHasContent = countRealUserFields(draft.formData) > 0;
          if (!currentHasContent && draftHasContent && !noteEmptyManuallySavedRef.current) {
            if (applySilentRestore(draft)) return;
          }

          // Jun 2026 — SILENT RESTORE WHEN SAFE: a local draft that is newer
          // than the server and doesn't blank out saved content restores
          // silently (loading → restored → saved), no prompt.  Prompt only
          // in ambiguous/risky cases:
          //   • server draft from another user (admin recovery semantics)
          //   • server advanced past the state we beacon-flushed from
          //     (someone/something else saved in between)
          //   • the draft would empty a meaningful share of saved fields
          if (draft.source !== 'server' && draftHasContent) {
            const serverAdvancedSinceFlush = !!(
              draft.flushStamp?.serverUpdatedAt &&
              serverUpdatedAtRef.current &&
              draft.flushStamp.serverUpdatedAt !== serverUpdatedAtRef.current
            );
            const loss = computeDraftLoss(formDataRef.current, draft.formData);
            void 0 && console.log('[Autosave] Silent-restore safety check:', {
              serverAdvancedSinceFlush,
              lost: loss.lost,
              serverFilled: loss.serverFilled,
              risky: loss.isRisky,
            });
            if (!serverAdvancedSinceFlush && !loss.isRisky) {
              if (applySilentRestore(draft)) return;
            }
          }
        } catch (autoErr) {
          void 0 && console.log('[Autosave] auto-restore check failed, falling back to banner', autoErr);
        }

        // Jun 2026 — NO RESTORE DIALOG on note open: any draft that passed
        // validation is restored silently (the note is still held in its
        // loading state via draftRestorePending, so the user only ever sees
        // the restored content).  The banner remains ONLY as a last resort
        // when the host provides no onFormDataRestore handler.
        if (applySilentRestore(draft)) return;

        const source = draft.source === 'server' 
          ? `server (from ${draft.userName || 'another user'})` 
          : 'local storage';
        void 0 && console.log(`[Autosave] ⚠️ Showing recovery banner for draft from ${source}`);
        setRecoveredDraft(draft);
        setShowDraftRecovery(true);
      }
      } finally {
        // Always release the loading hold — restore (if any) has been applied
        // synchronously to host state by this point.
        setDraftRestorePending(false);
      }
    }, 1500); // 1.5 seconds to ensure server data is fully loaded
    
    return () => clearTimeout(timeoutId);
  }, [noteId, enabled, checkForLocalDraft, checkForServerDraft, hasMeaningfulDifference, clearLocalDraft, applySilentRestore]);
  
  // Cleanup on unmount - CRITICAL: Save pending changes before destroying timer
  // IMPORTANT: Capture refs BEFORE clearing timer to avoid empty state
  useEffect(() => {
    return () => {
      // CAPTURE REFS FIRST before any cleanup - state may clear during unmount
      const capturedData = formDataRef.current;
      const capturedConsent = consentDataRef.current;
      const capturedGoals = linkedGoalsRef.current;
      const capturedNoteId = noteIdRef.current;
      const capturedVersion = currentVersionRef.current;
      const capturedSessionId = sessionIdRef.current;
      const hasPendingTimer = !!fieldBlurTimeoutRef.current;
      const wasInitialized = isInitializedRef.current;
      
      // Now clear the timer
      if (fieldBlurTimeoutRef.current) {
        clearTimeout(fieldBlurTimeoutRef.current);
        fieldBlurTimeoutRef.current = null;
      }
      // Workstream C3 hardening — also clear the debounced
      // cache-write timer.  The unmount path below does its own
      // synchronous localStorage cache write (uses sendBeacon for
      // the server) so a pending debounce here would be redundant
      // and could race the unmount cache write.
      if (cacheWriteTimeoutRef.current) {
        clearTimeout(cacheWriteTimeoutRef.current);
        cacheWriteTimeoutRef.current = null;
      }
      // Feb 2026 — also cancel any pending requestIdleCallback so a
      // zombie idle write cannot fire AFTER this unmount path's
      // synchronous localStorage write below.  Without this, the
      // idle callback could run with stale snapshot data and
      // clobber the fresh post-unmount cache key.
      if (idleCallbackRef.current) {
        try { idleCallbackRef.current(); } catch {}
        idleCallbackRef.current = null;
      }
      pendingCacheWriteRef.current = null;
      isDirtyForCacheRef.current = false;
      
      // Check if we have unsaved changes that need saving
      // Use captured values since refs may already be cleared
      if (hasPendingTimer && capturedData && capturedNoteId && wasInitialized) {
        const baselineData = initialServerDataRef.current || lastSavedFormDataRef.current;
        if (baselineData) {
          const currentHash = computeHash({ formData: capturedData, consentData: capturedConsent, linkedGoals: capturedGoals });
          if (currentHash !== lastSavedHashRef.current) {
            // There ARE unsaved changes - use sendBeacon for reliable delivery
            void 0 && console.log('[Autosave] ⚡ Cleanup: Pending changes detected, using sendBeacon');
            
            // 1. Cache to localStorage immediately
            try {
              const token = localStorage.getItem('token');
              const key = deriveEncryptionKey(token);
              const draftData = {
                noteId: capturedNoteId,
                formData: capturedData,
                consentData: capturedConsent || null,
                linkedGoals: capturedGoals || [],
                timestamp: new Date().toISOString(),
                sessionId: capturedSessionId,
              };
              const encrypted = encryptData(draftData, key);
              if (encrypted) {
                safeSetDraft(capturedNoteId, encrypted);
                // Update cached-hash tracker so a quick re-mount of
                // the same note doesn't redundantly re-encrypt this
                // payload on its first typing pause.
                lastCachedHashRef.current = computeHash(capturedData);
              }
            } catch (e) {
              // Silent fail
            }
            
            // 2. Use sendBeacon for server save (more reliable than silentSave during unmount)
            try {
              const token = localStorage.getItem('token');
              if (token) {
                const beaconPayload = JSON.stringify({
                  form_data: capturedData,
                  consent_data: capturedConsent,
                  linked_goals: capturedGoals,
                  consent_touched: changedFieldsRef.current.has('__consent_data__'),
                  save_trigger: 'cleanup_unmount',
                  session_id: capturedSessionId,
                  is_delta: false,
                  expected_version: capturedVersion,
                  auth_token: token,
                });
                const blob = new Blob([beaconPayload], { type: 'application/json' });
                const queued = navigator.sendBeacon(
                  `${process.env.REACT_APP_BACKEND_URL}/api/notes/${capturedNoteId}/beacon-save`,
                  blob
                );
                if (queued) stampBeaconFlush(capturedNoteId, serverUpdatedAtRef.current);
              }
            } catch (e) {
              // Silent fail - localStorage backup is our safety net
            }
          }
        }
      }
    };
  }, []);
  
  // Save before page unload - use sendBeacon for reliable delivery
  useEffect(() => {
    if (!enabled || !noteIdRef.current) return;
    
    const handleBeforeUnload = (event) => {
      const currentData = formDataRef.current;
      const currentNoteId = noteIdRef.current;
      
      // Skip if no data to save
      if (!currentData || !currentNoteId) return;
      
      // Check if there are unsaved changes worth saving
      const baselineData = initialServerDataRef.current || lastSavedFormDataRef.current;
      if (baselineData) {
        const currentHash = computeHash({ formData: currentData, consentData: consentDataRef.current, linkedGoals: linkedGoalsRef.current });
        if (currentHash === lastSavedHashRef.current) {
          // No changes - just cache locally as backup
          cacheDraftLocally(currentData, consentDataRef.current, linkedGoalsRef.current);
          return;
        }
      }
      
      // Cache locally first (always works)
      cacheDraftLocally(currentData, consentDataRef.current, linkedGoalsRef.current);
      
      // Use sendBeacon for reliable server save during page unload
      // sendBeacon queues the request even as the page closes
      try {
        const token = getAuthToken?.() || localStorage.getItem('token');
        if (token && currentNoteId) {
          const payload = JSON.stringify({
            form_data: currentData,
            consent_data: consentDataRef.current,
            linked_goals: linkedGoalsRef.current,
            consent_touched: changedFieldsRef.current.has('__consent_data__'),
            save_trigger: 'beforeunload',
            session_id: sessionIdRef.current,
            is_delta: false,
            expected_version: currentVersionRef.current,
          });
          
          // sendBeacon returns true if queued successfully
          const beaconUrl = `${process.env.REACT_APP_BACKEND_URL}/api/notes/${currentNoteId}/autosave`;
          const blob = new Blob([payload], { type: 'application/json' });
          
          // Note: sendBeacon doesn't support custom headers, so we need to include token in URL or body
          // For this implementation, we'll rely on the local cache as primary backup
          // and use a special endpoint that accepts token in body
          const beaconPayload = JSON.stringify({
            ...JSON.parse(payload),
            auth_token: token, // Include token in body for beacon requests
          });
          const beaconBlob = new Blob([beaconPayload], { type: 'application/json' });
          
          const queued = navigator.sendBeacon(
            `${process.env.REACT_APP_BACKEND_URL}/api/notes/${currentNoteId}/beacon-save`,
            beaconBlob
          );
          
          if (queued) {
            stampBeaconFlush(currentNoteId, serverUpdatedAtRef.current);
            void 0 && console.log('[Autosave] 🚀 Beacon save queued for', currentNoteId);
          } else {
            console.warn('[Autosave] ⚠️ Beacon save failed to queue, relying on local cache');
          }
        }
      } catch (e) {
        console.warn('[Autosave] Beacon save error:', e);
        // Local cache is our backup
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [enabled, noteId, cacheDraftLocally, getAuthToken]);
  
  // Function to update the version (called when note is loaded or after successful restore)
  const setVersion = useCallback((version) => {
    currentVersionRef.current = version;
    void 0 && console.log(`[Autosave] Version set to ${version}`);
  }, []);
  
  // Function to clear conflict state (after user resolves it)
  const clearConflict = useCallback(() => {
    setVersionConflict(null);
    setSaveStatus(SaveStatus.IDLE);
  }, []);
  
  /**
   * EXIT GUARD: Check if there are REAL unsaved changes
   * 
   * Simple approach: Compare current saveStatus and check if we have pending changes
   * - If saveStatus is SAVED or IDLE with no pending changes, allow exit
   * - If saveStatus is SAVING, there are pending changes
   * - If saveStatus is ERROR/CONFLICT, there are unsaved changes
   */
  const hasRealUnsavedChanges = useCallback(() => {
    // Offline unflushed-edit check MUST run before any allow-exit early
    // returns — the justSaved 3s suppression hid it on quick closes (iter517).
    if (!navigator.onLine) {
      if (lastSavedHashRef.current) {
        const combinedHash = computeHash({ formData: formDataRef.current, consentData: consentDataRef.current, linkedGoals: linkedGoalsRef.current });
        if (combinedHash !== lastSavedHashRef.current) {
          void 0 && console.log('[Autosave] Exit guard: offline with unflushed edits - blocking exit');
          return true;
        }
      } else if (initialServerDataRef.current && hasMeaningfulDifference(initialServerDataRef.current, formDataRef.current)) {
        // Never-saved note with edits and no network — flush/beacon can't land.
        void 0 && console.log('[Autosave] Exit guard: offline, never-saved note with edits - blocking exit');
        return true;
      }
    }
    // If we just saved successfully, allow exit
    if (justSavedRef.current) {
      void 0 && console.log('[Autosave] Exit guard: justSaved - allowing exit');
      return false;
    }
    
    // If save is in progress, there ARE unsaved changes
    if (saveStatus === SaveStatus.SAVING) {
      void 0 && console.log('[Autosave] Exit guard: save in progress - blocking exit');
      return true;
    }
    
    // If there was an error or conflict, there ARE unsaved changes
    if (saveStatus === SaveStatus.ERROR || saveStatus === SaveStatus.CONFLICT || saveStatus === SaveStatus.OFFLINE_CACHED) {
      void 0 && console.log('[Autosave] Exit guard: error/conflict state - blocking exit');
      return true;
    }
    
    // If status is SAVED, there are no unsaved changes (the offline
    // unflushed-edit case is handled at the top of this guard).
    if (saveStatus === SaveStatus.SAVED) {
      void 0 && console.log('[Autosave] Exit guard: status is SAVED - allowing exit');
      return false;
    }
    
    // For IDLE status, check if there's any data that hasn't been saved
    // Compare lastSavedFormDataRef to formDataRef
    if (!lastSavedFormDataRef.current && formDataRef.current) {
      // We have form data but never saved - check if form has real content
      const hasContent = formDataRef.current?.fields?.some(f => {
        const val = f.value;
        if (val === null || val === undefined || val === '') return false;
        if (Array.isArray(val) && val.length === 0) return false;
        return true;
      });
      if (hasContent) {
        void 0 && console.log('[Autosave] Exit guard: has content but never saved - blocking exit');
        return true;
      }
    }
    
    // Default: allow exit
    void 0 && console.log('[Autosave] Exit guard: default - allowing exit');
    return false;
  }, [saveStatus]);
  
  // FIX C: hasUnsavedChanges is now a getter that returns the ref value
  // This ensures it stays in sync with hasMeaningfulDifference
  const hasUnsavedChanges = hasUnsavedChangesRef.current;
  
  return {
    // Handlers
    onFieldBlur,
    forceSave,
    flushSave, // For explicit save before internal navigation (Close button)
    restoreFromDraft,
    dismissDraftRecovery,
    checkForServerDraft, // For manual server draft check (admin use)
    
    // Status
    saveStatus,
    lastSavedAt,
    isSaving: saveStatus === SaveStatus.SAVING,
    hasUnsavedChanges, // FIX C: Now derived from hasMeaningfulDifference comparison
    hasRealUnsavedChanges, // EXIT GUARD: True only if meaningful differences from server
    
    // Draft recovery
    showDraftRecovery,
    recoveredDraft,
    draftRestorePending, // Jun 2026 — hosts hold the note loading state while true
    draftSource: recoveredDraft?.source || null, // 'local' or 'server'
    draftUserName: recoveredDraft?.userName || null, // For server drafts, who created it
    
    // Optimistic Locking
    versionConflict,
    currentVersion: currentVersionRef.current,
    setVersion,
    clearConflict,
    hasConflict: saveStatus === SaveStatus.CONFLICT,
    
    // Utilities
    clearLocalDraft,
    sessionId: sessionIdRef.current,
    hasMeaningfulDifference, // Export for conflict handling to check if sync is needed
    
    // Recovery utilities for emergency data recovery
    getAllLocalDrafts: () => {
      const drafts = [];
      const token = getAuthToken?.() || localStorage.getItem('token');
      if (!token) return drafts;
      
      const key = deriveEncryptionKey(token);
      for (let i = 0; i < localStorage.length; i++) {
        const storageKey = localStorage.key(i);
        if (storageKey && storageKey.startsWith(STORAGE_PREFIX)) {
          try {
            const encrypted = localStorage.getItem(storageKey);
            const draft = decryptData(encrypted, key);
            if (draft && draft.noteId) {
              drafts.push({
                noteId: draft.noteId,
                timestamp: draft.timestamp,
                hasFormData: !!draft.formData,
                fieldCount: draft.formData?.fields?.length || 0,
              });
            }
          } catch (e) {
            // Skip invalid drafts
          }
        }
      }
      return drafts;
    },
  };
}

export default useNoteAutosave;
